import {
  U21RadioGroupOptionProps,
  U21SelectOptionProps,
} from 'app/shared/u21-ui/components';

export const FINTRAC_STR_DATE_FORMAT = 'yyyy-MM-dd';
export const FINTRAC_STR_TIME_FORMAT = 'HH:mm:ssxxx';
export const FINTRAC_STR_DATETIME_FORMAT = `${FINTRAC_STR_DATE_FORMAT} ${FINTRAC_STR_TIME_FORMAT}`;

const generateOptions = <TValue extends string | number>(
  obj: Record<string, U21SelectOptionProps<TValue>>,
): U21SelectOptionProps<TValue>[] => Object.values(obj).map((v) => v);

export const FINTRAC_STR_STARTING_ACTION_DIRECTION_OPTIONS: Readonly<
  U21RadioGroupOptionProps<number>[]
> = [
  {
    label: 'In',
    value: 1,
  },
  {
    label: 'Out',
    value: 2,
  },
] as const;

export const FINTRAC_STR_YES_NO_OPTIONS: Readonly<
  U21RadioGroupOptionProps<boolean>[]
> = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
] as const;

export const FINTRAC_STR_ACTIVITY_SECTOR_CODES = {
  ACCOUNTANT: {
    value: 1,
    text: 'Accountant',
  },
  BANK: {
    value: 2,
    text: 'Bank',
  },
  CAISSE_POPULAIRE: {
    value: 3,
    text: 'Caisse populaire',
  },
  CROWN_AGENT: {
    value: 4,
    text: 'Crown agent',
  },
  CASINO: {
    value: 5,
    text: 'Casino',
  },
  CO_OP_CREDIT_SOCIETY: {
    value: 6,
    text: 'Co-op credit society',
  },
  LIFE_INSURANCE_BROKER_OR_AGENT: {
    value: 9,
    text: 'Life insurance broker or agent',
  },
  LIFE_INSURANCE_COMPANY: {
    value: 10,
    text: 'Life insurance company',
  },
  MONEY_SERVICES_BUSINESS: {
    value: 11,
    text: 'Money services business',
  },
  PROVINCIAL_SAVINGS_OFFICE: {
    value: 12,
    text: 'Provincial savings office',
  },
  REAL_ESTATE: {
    value: 13,
    text: 'Real estate',
  },
  CREDIT_UNION: {
    value: 14,
    text: 'Credit union',
  },
  SECURITIES_DEALER: {
    value: 15,
    text: 'Securities dealer',
  },
  TRUST_AND_OR_LOAN_COMPANY: {
    value: 16,
    text: 'Trust and/or loan company',
  },
  BRITISH_COLUMBIA_NOTARY: {
    value: 17,
    text: 'British Columbia notary',
  },
  DEALER_IN_PRECIOUS_METALS_AND_STONES: {
    value: 18,
    text: 'Dealer in precious metals and stones',
  },
  CREDIT_UNION_CENTRAL: {
    value: 19,
    text: 'Credit union central',
  },
  FINANCIAL_SERVICES_COOPERATIVE: {
    value: 20,
    text: 'Financial services cooperative',
  },
  FOREIGN_MONEY_SERVICES_BUSINESS: {
    value: 21,
    text: 'Foreign money services business',
  },
  MORTGAGE_ADMINISTRATORS: {
    value: 22,
    text: 'Mortgage administrators',
  },
  ARMOURED_CARS: {
    value: 23,
    text: 'Armoured Cars',
  },
  MORTGAGE_BROKERS: {
    value: 24,
    text: 'Mortgage brokers',
  },
  MORTGAGE_LENDERS: {
    value: 25,
    text: 'Mortgage lenders',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_ACTIVITY_SECTOR_CODES_OPTIONS =
  generateOptions<number>(FINTRAC_STR_ACTIVITY_SECTOR_CODES);

export const FINTRAC_STR_SUSPICION_TYPE_CODES = {
  MONEY_LAUNDERING: {
    value: 1,
    text: 'Money laundering',
  },
  TERRORIST_FINANCING: {
    value: 2,
    text: 'Terrorist financing',
  },
  MONEY_LAUNDERING_AND_TERRORIST_FINANCING: {
    value: 3,
    text: 'Money laundering and terrorist financing',
  },
  SANCTIONS_EVASION: {
    value: 4,
    text: 'Sanctions evasion',
  },
  MONEY_LAUNDERING_AND_SANCTIONS_EVASION: {
    value: 5,
    text: 'Money laundering and sanctions evasion',
  },
  TERRORIST_FINANCING_AND_SANCTIONS_EVASION: {
    value: 6,
    text: 'Terrorist financing and sanctions evasion',
  },
  MONEY_LAUNDERING_TERRORIST_FINANCING_SANCTIONS_EVASION: {
    value: 7,
    text: 'Money laundering/Terrorist financing/Sanctions evasion',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_SUSPICION_TYPE_CODES_OPTIONS = generateOptions<number>(
  FINTRAC_STR_SUSPICION_TYPE_CODES,
);

export const FINTRAC_STR_PROJECT_CODES = {
  PROJECT_ANTON: {
    value: 1,
    text: 'Project ANTON',
  },
  PROJECT_ATHENA: {
    value: 2,
    text: 'Project ATHENA',
  },
  PROJECT_CHAMELEON: {
    value: 3,
    text: 'Project CHAMELEON',
  },
  PROJECT_GUARDIAN: {
    value: 5,
    text: 'Project GUARDIAN',
  },
  PROJECT_LEGION: {
    value: 6,
    text: 'Project LEGION',
  },
  PROJECT_PROTECT: {
    value: 7,
    text: 'Project PROTECT',
  },
  PROJECT_SHADOW: {
    value: 8,
    text: 'Project SHADOW',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_PROJECT_CODES_OPTIONS = generateOptions<number>(
  FINTRAC_STR_PROJECT_CODES,
);

export const FINTRAC_STR_MINISTERIAL_DIRECTIVE_CODES = {
  IR2020: {
    value: 'IR2020',
    text: 'IR2020',
  },
} as const satisfies Record<string, U21SelectOptionProps<string>>;

export const FINTRAC_STR_MINISTERIAL_DIRECTIVE_CODES_OPTIONS =
  generateOptions<string>(FINTRAC_STR_MINISTERIAL_DIRECTIVE_CODES);

export const FINTRAC_STR_TRANSACTION_METHOD_CODES = {
  IN_PERSON: {
    value: 1,
    text: 'In person',
  },
  AUTOMATED_BANKING_MACHINE: {
    value: 2,
    text: 'Automated banking machine',
  },
  ARMOURED_CAR: {
    value: 3,
    text: 'Armoured car',
  },
  COURIER: {
    value: 4,
    text: 'Courier',
  },
  MAIL_DEPOSIT: {
    value: 5,
    text: 'Mail deposit',
  },
  TELEPHONE: {
    value: 6,
    text: 'Telephone',
  },
  NIGHT_DEPOSIT: {
    value: 8,
    text: 'Night deposit',
  },
  QUICK_DROP: {
    value: 9,
    text: 'Quick drop',
  },
  SELF_REDEMPTION_KIOSK: {
    value: 10,
    text: 'Self-redemption kiosk',
  },
  VIRTUAL_CURRENCY_ATM: {
    value: 11,
    text: 'Virtual currency ATM',
  },
  ONLINE: {
    value: 12,
    text: 'Online',
  },
  OTHER: {
    value: 7,
    text: 'Other',
  }, // Other is intentionally placed at the end of the list for better UX
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_TRANSACTION_METHOD_CODES_OPTIONS =
  generateOptions<number>(FINTRAC_STR_TRANSACTION_METHOD_CODES);

export const FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE = {
  BANK_DRAFT: {
    value: 1,
    text: 'Bank draft',
  },
  CASH: {
    value: 2,
    text: 'Cash',
  },
  CASINO_PRODUCT: {
    value: 3,
    text: 'Casino product',
  },
  CHEQUE: {
    value: 4,
    text: 'Cheque',
  },
  DOMESTIC_FUNDS_TRANSFER: {
    value: 5,
    text: 'Domestic funds transfer',
  },
  EMAIL_MONEY_TRANSFER: {
    value: 6,
    text: 'Email money transfer',
  },
  INTERNATIONAL_FUNDS_TRANSFER: {
    value: 8,
    text: 'International funds transfer',
  },
  INVESTMENT_PRODUCT: {
    value: 9,
    text: 'Investment product',
  },
  JEWELLERY: {
    value: 10,
    text: 'Jewellery',
  },
  MOBILE_MONEY_TRANSFER: {
    value: 11,
    text: 'Mobile money transfer',
  },
  MONEY_ORDER: {
    value: 12,
    text: 'Money order',
  },
  PRECIOUS_METALS: {
    value: 13,
    text: 'Precious metals',
  },
  PRECIOUS_STONES: {
    value: 14,
    text: 'Precious stones',
  },
  VIRTUAL_CURRENCY: {
    value: 16,
    text: 'Virtual currency',
  },
  OTHER: {
    value: 17,
    text: 'Other',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS =
  generateOptions<number>(FINTRAC_STR_IN_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE);

export const FINTRAC_STR_OUT_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE = {
  CASINO_PRODUCT: {
    value: 3,
    text: 'Casino product',
  },
  FUNDS_WITHDRAWAL: {
    value: 7,
    text: 'Funds withdrawal',
  },
  INVESTMENT_PRODUCT: {
    value: 9,
    text: 'Investment product',
  },
  VIRTUAL_CURRENCY: {
    value: 16,
    text: 'Virtual currency',
  },
  OTHER: {
    value: 17,
    text: 'Other',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_OUT_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS =
  generateOptions<number>(
    FINTRAC_STR_OUT_FUND_ASSET_VIRTUAL_CURRENCY_TYPE_CODE,
  );

export const FINTRAC_STR_HOLDER_TYPE_CODE = {
  PERSON_NAME: {
    value: 1,
    text: 'Person name',
  },
  ENTITY_NAME: {
    value: 2,
    text: 'Entity name',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_HOLDER_TYPE_CODE_OPTIONS = generateOptions<number>(
  FINTRAC_STR_HOLDER_TYPE_CODE,
);

export const FINTRAC_STR_BENEFICIARY_TYPE_CODE = {
  PERSON_DETAILS: {
    value: 3,
    text: 'Person details',
  },
  ENTITY_DETAILS: {
    value: 4,
    text: 'Entity details',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_BENEFICIARY_TYPE_CODE_OPTIONS =
  generateOptions<number>(FINTRAC_STR_BENEFICIARY_TYPE_CODE);

export const FINTRAC_STR_CONDUCTOR_TYPE_CODE = {
  PERSON_AND_EMPLOYER_DETAILS: {
    value: 5,
    text: 'Person and employer Details',
  },
  ENTITY_AND_BENEFICIAL_OWNERSHIP_DETAILS: {
    value: 6,
    text: 'Entity and beneficial ownership details',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_CONDUCTOR_TYPE_CODE_OPTIONS = generateOptions<number>(
  FINTRAC_STR_CONDUCTOR_TYPE_CODE,
);

export const FINTRAC_STR_DEFINITION_TYPE_CODE = {
  ...FINTRAC_STR_HOLDER_TYPE_CODE,
  ...FINTRAC_STR_BENEFICIARY_TYPE_CODE,
  ...FINTRAC_STR_CONDUCTOR_TYPE_CODE,
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_DEFINITION_TYPE_CODE_MAP = Object.values(
  FINTRAC_STR_DEFINITION_TYPE_CODE,
).reduce<Record<number, string>>((acc, curr) => {
  acc[curr.value] = curr.text;
  return acc;
}, {});

export const FINTRAC_STR_DEFINITION_TYPE_CODE_OPTIONS = generateOptions<number>(
  FINTRAC_STR_DEFINITION_TYPE_CODE,
);

export const FINTRAC_STR_CURRENCY_CODES = {
  CAD: { value: 'CAD', text: '[CAD] Canadian Dollar' },
  USD: { value: 'USD', text: '[USD] United States Dollar' },
  ADP: { value: 'ADP', text: '[ADP] Andorran Peseta' },
  AED: { value: 'AED', text: '[AED] United Arab Emirates Dirham' },
  AFA: { value: 'AFA', text: '[AFA] Afghani' },
  AFN: { value: 'AFN', text: '[AFN] Afghani' },
  ALL: { value: 'ALL', text: '[ALL] Lek (Albania)' },
  AMD: { value: 'AMD', text: '[AMD] Armenian Dram' },
  ANG: { value: 'ANG', text: '[ANG] Netherlands Antillian Guilder' },
  AOA: { value: 'AOA', text: '[AOA] Kwanza (Angola)' },
  AOK: { value: 'AOK', text: '[AOK] Kwanza' },
  AON: { value: 'AON', text: '[AON] New Kwanza' },
  ARP: { value: 'ARP', text: '[ARP] Argentine Peso' },
  ARS: { value: 'ARS', text: '[ARS] Argentine Peso' },
  ATS: { value: 'ATS', text: '[ATS] Schilling (Austria)' },
  AUD: { value: 'AUD', text: '[AUD] Australian Dollar' },
  AWF: { value: 'AWF', text: '[AWF] Aruba Florin' },
  AWG: { value: 'AWG', text: '[AWG] Aruban Guilder' },
  AZM: { value: 'AZM', text: '[AZM] Azerbaijanian Manat' },
  AZN: { value: 'AZN', text: '[AZN] Azerbaijan Manat' },
  BAK: { value: 'BAK', text: '[BAK] Convertible Mark' },
  BAM: { value: 'BAM', text: '[BAM] Convert. Marks (Bosnia/Herzegovina)' },
  BBD: { value: 'BBD', text: '[BBD] Barbados Dollar' },
  BDT: { value: 'BDT', text: '[BDT] Taka (Bangladesh)' },
  BEC: { value: 'BEC', text: '[BEC] Belgian Franc (convertible)' },
  BEF: { value: 'BEF', text: '[BEF] Belgian Franc' },
  BEL: { value: 'BEL', text: '[BEL] Belgian Franc (financial)' },
  BGL: { value: 'BGL', text: '[BGL] Lev' },
  BGN: { value: 'BGN', text: '[BGN] Bulgarian Lev' },
  BHD: { value: 'BHD', text: '[BHD] Bahraini Dinar' },
  BIF: { value: 'BIF', text: '[BIF] Burundi Franc' },
  BMD: { value: 'BMD', text: '[BMD] Bermudian Dollar' },
  BND: { value: 'BND', text: '[BND] Brunei Dollar' },
  BOB: { value: 'BOB', text: '[BOB] Boliviano' },
  BOP: { value: 'BOP', text: '[BOP] Bolivian Peso' },
  BOV: { value: 'BOV', text: '[BOV] Mvdol (Bolivia)' },
  BRC: { value: 'BRC', text: '[BRC] Cruzeiro' },
  BRL: { value: 'BRL', text: '[BRL] Brazilian Real' },
  BSD: { value: 'BSD', text: '[BSD] Bahamian Dollar' },
  BTN: { value: 'BTN', text: '[BTN] Ngultrum (Bhutan)' },
  BTR: { value: 'BTR', text: '[BTR] Bhutan Rupee' },
  BUK: { value: 'BUK', text: '[BUK] Kyat' },
  BWP: { value: 'BWP', text: '[BWP] Pula (Botswana)' },
  BYN: { value: 'BYN', text: '[BYN] Belarusian Ruble' },
  BYR: { value: 'BYR', text: '[BYR] Belarusian Ruble' },
  BZD: { value: 'BZD', text: '[BZD] Belize Dollar' },
  CDF: { value: 'CDF', text: '[CDF] Congolese Franc' },
  CDZ: { value: 'CDZ', text: '[CDZ] New Zaire' },
  CHE: { value: 'CHE', text: '[CHE] WIR Euro (Suisse)' },
  CHF: { value: 'CHF', text: '[CHF] Swiss Franc (Liechtenstein)' },
  CHW: { value: 'CHW', text: '[CHW] WIR Franc Switzerland' },
  CLF: { value: 'CLF', text: '[CLF] Unidades de Formento (Chile)' },
  CLP: { value: 'CLP', text: '[CLP] Chilean Peso' },
  CNY: { value: 'CNY', text: '[CNY] Yuan Renminbi (China)' },
  COP: { value: 'COP', text: '[COP] Colombian Peso' },
  COU: { value: 'COU', text: '[COU] Unidad de Valor Real (Columbia)' },
  CRC: { value: 'CRC', text: '[CRC] Costa Rican Colon' },
  CSD: { value: 'CSD', text: '[CSD] Serbian Dinar' },
  CSK: { value: 'CSK', text: '[CSK] Koruna (Czechoslovakia)' },
  CUC: { value: 'CUC', text: '[CUC] Peso Convertible' },
  CUP: { value: 'CUP', text: '[CUP] Cuban Peso' },
  CVE: { value: 'CVE', text: '[CVE] Cape Verde Escudo' },
  CYP: { value: 'CYP', text: '[CYP] Cyprus Pound' },
  CZK: { value: 'CZK', text: '[CZK] Czech Koruna' },
  DDM: { value: 'DDM', text: '[DDM] Mark der DDR' },
  DEM: { value: 'DEM', text: '[DEM] Deutsche Mark' },
  DJF: { value: 'DJF', text: '[DJF] Djibouti Franc' },
  DKK: { value: 'DKK', text: '[DKK] Danish Krone' },
  DOP: { value: 'DOP', text: '[DOP] Dominican Peso' },
  DZD: { value: 'DZD', text: '[DZD] Algerian Dinar' },
  ECS: { value: 'ECS', text: '[ECS] Sucre' },
  EEK: { value: 'EEK', text: '[EEK] Kroon (Estonia)' },
  EGP: { value: 'EGP', text: '[EGP] Egyptian Pound' },
  ERN: { value: 'ERN', text: '[ERN] Nakfa (Eritrea)' },
  ESP: { value: 'ESP', text: '[ESP] Spanish Peseta' },
  ETB: { value: 'ETB', text: '[ETB] Ethiopian Birr' },
  EUR: { value: 'EUR', text: '[EUR] Euro' },
  FIM: { value: 'FIM', text: '[FIM] Markka' },
  FJD: { value: 'FJD', text: '[FJD] Fiji Dollar' },
  FKP: { value: 'FKP', text: '[FKP] Falkland Islands Pound' },
  FRF: { value: 'FRF', text: '[FRF] French Franc' },
  GBP: { value: 'GBP', text: '[GBP] Pound Sterling (United Kingdom)' },
  GEL: { value: 'GEL', text: '[GEL] Lari (Georgia)' },
  GHC: { value: 'GHC', text: '[GHC] Cedi (Ghana)' },
  GHS: { value: 'GHS', text: '[GHS] Cedi (Ghana)' },
  GIP: { value: 'GIP', text: '[GIP] Gibraltar Pound' },
  GMD: { value: 'GMD', text: '[GMD] Dalasi (Gambia)' },
  GNF: { value: 'GNF', text: '[GNF] Guinean Franc' },
  GNS: { value: 'GNS', text: '[GNS] Syli' },
  GQE: { value: 'GQE', text: '[GQE] Ekwele' },
  GRD: { value: 'GRD', text: '[GRD] Drachma' },
  GTQ: { value: 'GTQ', text: '[GTQ] Quetzal (Guatemala)' },
  GWP: { value: 'GWP', text: '[GWP] Guinea-Bissau Peso' },
  GYD: { value: 'GYD', text: '[GYD] Guyana Dollar' },
  HKD: { value: 'HKD', text: '[HKD] Hong Kong Dollar' },
  HNL: { value: 'HNL', text: '[HNL] Lempira (Honduras)' },
  HRK: { value: 'HRK', text: '[HRK] Croatian Kuna' },
  HTG: { value: 'HTG', text: '[HTG] Gourde (Haiti)' },
  HUF: { value: 'HUF', text: '[HUF] Forint (Hungary)' },
  IDR: { value: 'IDR', text: '[IDR] Rupiah (Indonesia)' },
  IEP: { value: 'IEP', text: '[IEP] Irish Pound' },
  ILS: { value: 'ILS', text: '[ILS] New Israeli Shekel' },
  INR: { value: 'INR', text: '[INR] Indian Rupee' },
  IQD: { value: 'IQD', text: '[IQD] Iraqi Dinar' },
  IRR: { value: 'IRR', text: '[IRR] Iranian Rial' },
  ISK: { value: 'ISK', text: '[ISK] Iceland Krona' },
  ITL: { value: 'ITL', text: '[ITL] Lira' },
  JMD: { value: 'JMD', text: '[JMD] Jamaican Dollar' },
  JOD: { value: 'JOD', text: '[JOD] Jordanian Dinar' },
  JPY: { value: 'JPY', text: '[JPY] Yen (Japan)' },
  KES: { value: 'KES', text: '[KES] Kenyan Shilling' },
  KGS: { value: 'KGS', text: '[KGS] Som (Kyrgyzstan)' },
  KHR: { value: 'KHR', text: '[KHR] Riel (Cambodia)' },
  KMF: { value: 'KMF', text: '[KMF] Comorian Franc' },
  KPW: { value: 'KPW', text: '[KPW] North Korean Won' },
  KRW: { value: 'KRW', text: '[KRW] Won (South Korea)' },
  KWD: { value: 'KWD', text: '[KWD] Kuwaiti Dinar' },
  KYD: { value: 'KYD', text: '[KYD] Cayman Islands Dollar' },
  KZT: { value: 'KZT', text: '[KZT] Tenge (Kazakstan)' },
  LAK: { value: 'LAK', text: '[LAK] Lao Kip' },
  LBP: { value: 'LBP', text: '[LBP] Lebanese Pound' },
  LKR: { value: 'LKR', text: '[LKR] Sri Lankan Rupee' },
  LRD: { value: 'LRD', text: '[LRD] Liberian Dollar' },
  LSL: { value: 'LSL', text: '[LSL] Loti (Lesotho)' },
  LSM: { value: 'LSM', text: '[LSM] Maloti' },
  LTL: { value: 'LTL', text: '[LTL] Lithuanian Litas' },
  LUF: { value: 'LUF', text: '[LUF] Luxembourg Franc' },
  LVL: { value: 'LVL', text: '[LVL] Latvian Lat' },
  LYD: { value: 'LYD', text: '[LYD] Libyan Dinar' },
  MAD: { value: 'MAD', text: '[MAD] Moroccan Dirham' },
  MDL: { value: 'MDL', text: '[MDL] Moldovan Leu' },
  MGA: { value: 'MGA', text: '[MGA] Malagasy Ariary (Madagascar)' },
  MGF: { value: 'MGF', text: '[MGF] Malagasy Franc' },
  MKD: { value: 'MKD', text: '[MKD] Denar (Macedonia)' },
  MLF: { value: 'MLF', text: '[MLF] Mali Franc' },
  MMK: { value: 'MMK', text: '[MMK] Kyat (Myanmar)' },
  MNT: { value: 'MNT', text: '[MNT] Tugrik (Mongolia)' },
  MOP: { value: 'MOP', text: '[MOP] Pataca (Macau)' },
  MRO: { value: 'MRO', text: '[MRO] Ouguiya (Mauritania)' },
  MRU: { value: 'MRU', text: '[MRU] Ouguiya' },
  MTL: { value: 'MTL', text: '[MTL] Maltese Lira' },
  MTP: { value: 'MTP', text: '[MTP] Maltese Pound' },
  MUR: { value: 'MUR', text: '[MUR] Mauritian Rupee' },
  MVR: { value: 'MVR', text: '[MVR] Rufiyaa (Maldives)' },
  MWK: { value: 'MWK', text: '[MWK] Malawi Kwacha' },
  MXN: { value: 'MXN', text: '[MXN] Mexcian Peso' },
  MXP: { value: 'MXP', text: '[MXP] Mexican Peso' },
  MXV: { value: 'MXV', text: '[MXV] Mexican Unidad de Inversion' },
  MYR: { value: 'MYR', text: '[MYR] Malaysian Ringgit' },
  MZM: { value: 'MZM', text: '[MZM] Metical (Mosambique)' },
  MZN: { value: 'MZN', text: '[MZN] Metical (Mozambique)' },
  NAD: { value: 'NAD', text: '[NAD] Namibian Dollar' },
  NGN: { value: 'NGN', text: '[NGN] Naira (Nigeria)' },
  NIC: { value: 'NIC', text: '[NIC] Cordoba' },
  NIO: { value: 'NIO', text: '[NIO] Cordoba Oro' },
  NLG: { value: 'NLG', text: '[NLG] Netherlands Guilder' },
  NOK: { value: 'NOK', text: '[NOK] Norwegian Krone' },
  NPR: { value: 'NPR', text: '[NPR] Nepalese Rupee' },
  NZD: { value: 'NZD', text: '[NZD] New Zealand Dollar' },
  OMR: { value: 'OMR', text: '[OMR] Omani Rial' },
  PAB: { value: 'PAB', text: '[PAB] Balboa (Panama)' },
  PEN: { value: 'PEN', text: '[PEN] Sol' },
  PES: { value: 'PES', text: '[PES] Sol' },
  PGK: { value: 'PGK', text: '[PGK] Kina (Papua New Guinea)' },
  PHP: { value: 'PHP', text: '[PHP] Philippine Peso' },
  PKR: { value: 'PKR', text: '[PKR] Pakistan Rupee' },
  PLN: { value: 'PLN', text: '[PLN] Zloty (Poland)' },
  PLZ: { value: 'PLZ', text: '[PLZ] Zloty' },
  PTE: { value: 'PTE', text: '[PTE] Portugese Escudo' },
  PYG: { value: 'PYG', text: '[PYG] Guarani (Paraguay)' },
  QAR: { value: 'QAR', text: '[QAR] Qatari Rial' },
  ROL: { value: 'ROL', text: '[ROL] Old Leu (Romania)' },
  RON: { value: 'RON', text: '[RON] New Leu (Romania)' },
  RSD: { value: 'RSD', text: '[RSD] Serbian Dinar' },
  RUB: { value: 'RUB', text: '[RUB] Russian Ruble' },
  RUR: { value: 'RUR', text: '[RUR] Russian Ruble' },
  RWF: { value: 'RWF', text: '[RWF] Rwandan Franc' },
  SAR: { value: 'SAR', text: '[SAR] Saudi Riyal' },
  SBD: { value: 'SBD', text: '[SBD] Solomon Islands Dollar' },
  SBL: { value: 'SBL', text: '[SBL] Luigino' },
  SCR: { value: 'SCR', text: '[SCR] Seychelles Rupee' },
  SDD: { value: 'SDD', text: '[SDD] Sudanese Dinar' },
  SDG: { value: 'SDG', text: '[SDG] Sudanese Pound' },
  SDP: { value: 'SDP', text: '[SDP] Sudanese Pound' },
  SEK: { value: 'SEK', text: '[SEK] Swedish Krona' },
  SGD: { value: 'SGD', text: '[SGD] Singapore Dollar' },
  SHP: { value: 'SHP', text: '[SHP] Saint Helena Pound' },
  SIT: { value: 'SIT', text: '[SIT] Tolar (Slovenia)' },
  SKK: { value: 'SKK', text: '[SKK] Slovak Koruna' },
  SLE: { value: 'SLE', text: '[SLE] Leone (Sierra Leone)' },
  SLL: { value: 'SLL', text: '[SLL] Leone (Sierra Leone)' },
  SOS: { value: 'SOS', text: '[SOS] Somalian Shilling' },
  SRD: { value: 'SRD', text: '[SRD] Surinam Dollar' },
  SRG: { value: 'SRG', text: '[SRG] Suriname Guilder' },
  SSP: { value: 'SSP', text: '[SSP] South Sudanese Pound' },
  STD: { value: 'STD', text: '[STD] Dobra (Sao Tome and Principa)' },
  STN: { value: 'STN', text: '[STN] Dobra' },
  SUR: { value: 'SUR', text: '[SUR] Ruble' },
  SVC: { value: 'SVC', text: '[SVC] El Salvador Colon' },
  SYP: { value: 'SYP', text: '[SYP] Syrian Pound' },
  SZL: { value: 'SZL', text: '[SZL] Lilangeni (Swaziland)' },
  THB: { value: 'THB', text: '[THB] Baht (Thailand)' },
  TJR: { value: 'TJR', text: '[TJR] Tajik Ruble' },
  TJS: { value: 'TJS', text: '[TJS] Somoni (Tajikistan)' },
  TMM: { value: 'TMM', text: '[TMM] Manat (Turkmenistan)' },
  TMT: { value: 'TMT', text: '[TMT] Manat (Turkmenistan)' },
  TND: { value: 'TND', text: '[TND] Tunisian Dinar' },
  TOP: { value: 'TOP', text: "[TOP] Pa'anga (Tonga)" },
  TPE: { value: 'TPE', text: '[TPE] Timor Escudo' },
  TRL: { value: 'TRL', text: '[TRL] Old Turkish Lira' },
  TRY: { value: 'TRY', text: '[TRY] New Turkish Lira' },
  TTD: { value: 'TTD', text: '[TTD] Trinidad and Tobago Dollar' },
  TWD: { value: 'TWD', text: '[TWD] New Taiwan Dollar' },
  TZS: { value: 'TZS', text: '[TZS] Tanzanian Shilling' },
  UAH: { value: 'UAH', text: '[UAH] Hryvnia (Ukraine)' },
  UGS: { value: 'UGS', text: '[UGS] Uganda Shilling' },
  UGX: { value: 'UGX', text: '[UGX] Uganda Shilling' },
  USN: { value: 'USN', text: '[USN] United States Dollar, next day' },
  USS: { value: 'USS', text: '[USS] US Dollar (same day)' },
  UYI: { value: 'UYI', text: '[UYI] Uruguay Peso en Unidades Indexadas' },
  UYP: { value: 'UYP', text: '[UYP] Uruguayan Peso' },
  UYU: { value: 'UYU', text: '[UYU] Uruguayan Peso' },
  UYW: { value: 'UYW', text: '[UYW] Unidad Previsional' },
  UZS: { value: 'UZS', text: '[UZS] Som (Uzbekistan)' },
  VEB: { value: 'VEB', text: '[VEB] Bolivar (Venezuela)' },
  VEF: { value: 'VEF', text: '[VEF] Bolívar' },
  VES: { value: 'VES', text: '[VES] Bolívar Soberano' },
  VND: { value: 'VND', text: '[VND] Dong (Vietnam)' },
  VUV: { value: 'VUV', text: '[VUV] Vatu (Vanuatu)' },
  WST: { value: 'WST', text: '[WST] Tala (Samoa)' },
  XAF: { value: 'XAF', text: '[XAF] CFA Franc BEAC' },
  XAG: { value: 'XAG', text: '[XAG] Silver (in ounces)' },
  XAU: { value: 'XAU', text: '[XAU] Gold (in ounces)' },
  XBA: { value: 'XBA', text: '[XBA] European Composite Unit (EURCO)' },
  XBB: { value: 'XBB', text: '[XBB] European Monetary Unit (E.M.U. - 6)' },
  XBC: {
    value: 'XBC',
    text: '[XBC] European Unit of Account 9 (E.U.A. - 9)',
  },
  XBD: { value: 'XBD', text: '[XBD] European Unit of Account 17' },
  XBT: { value: 'XBT', text: '[XBT] Bitcoin' },
  XCD: { value: 'XCD', text: '[XCD] East Caribbean Dollar' },
  XDR: { value: 'XDR', text: '[XDR] Special Drawing Rights (IMF)' },
  XEU: { value: 'XEU', text: '[XEU] European Currency Unit' },
  XFO: {
    value: 'XFO',
    text: '[XFO] Gold-Franc (Special Settlement Currency)',
  },
  XFU: {
    value: 'XFU',
    text: '[XFU] UIC-Franc (Special Settlement Currency)',
  },
  XOF: { value: 'XOF', text: '[XOF] CFA Franc BCEAO' },
  XPD: { value: 'XPD', text: '[XPD] Palladium (in ounces)' },
  XPF: { value: 'XPF', text: '[XPF] CFP Franc (French Polynesia)' },
  XPT: { value: 'XPT', text: '[XPT] Platinum (in ounces)' },
  XSU: { value: 'XSU', text: '[XSU] SUCRE' },
  XXX: { value: 'XXX', text: '[XXX] No currency code' },
  YDD: { value: 'YDD', text: '[YDD] Yemeni Dinar' },
  YER: { value: 'YER', text: '[YER] Yemeni Rial' },
  YUD: { value: 'YUD', text: '[YUD] Yugoslavian Dinar' },
  YUM: { value: 'YUM', text: '[YUM] New Dinar' },
  YUN: { value: 'YUN', text: '[YUN] Yugoslav New Dinar' },
  ZAL: { value: 'ZAL', text: '[ZAL] Rand (financial)' },
  ZAR: { value: 'ZAR', text: '[ZAR] Rand (Lesotho, Namibia, South Africa)' },
  ZMK: { value: 'ZMK', text: '[ZMK] Kwacha (Zambia)' },
  ZMW: { value: 'ZMW', text: '[ZMW] Kwacha (Zambia)' },
  ZWD: { value: 'ZWD', text: '[ZWD] Zimbabwe Dollar' },
  ZWL: { value: 'ZWL', text: '[ZWL] Zimbabwe Dollar' },
  ZZZ: { value: 'ZZZ', text: '[ZZZ] Unknown' },
} as const satisfies Record<string, U21SelectOptionProps<string>>;

export const FINTRAC_STR_CURRENCY_CODES_OPTIONS = generateOptions(
  FINTRAC_STR_CURRENCY_CODES,
);

export const FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE = {
  '1INCH': { value: '1INCH', text: '[1INCH] 1inch Network' },
  AAVE: { value: 'AAVE', text: '[AAVE] Aave' },
  ABBC: { value: 'ABBC', text: '[ABBC] ABBC Coin' },
  ADA: { value: 'ADA', text: '[ADA] Cardano' },
  ADX: { value: 'ADX', text: '[ADX] AdEx Network' },
  AE: { value: 'AE', text: '[AE] Aeternity' },
  AERGO: { value: 'AERGO', text: '[AERGO] Aergo' },
  AGI: { value: 'AGI', text: '[AGI] SingularityNET' },
  AGVC: { value: 'AGVC', text: '[AGVC] AgaveCoin' },
  AIB: { value: 'AIB', text: '[AIB] Advanced Internet Blocks' },
  AION: { value: 'AION', text: '[AION] Aion' },
  AKRO: { value: 'AKRO', text: '[AKRO] Akropolis' },
  ALGO: { value: 'ALGO', text: '[ALGO] Algorand' },
  AMO: { value: 'AMO', text: '[AMO] AMO Coin' },
  AMP: { value: 'AMP', text: '[AMP] Amp' },
  AMPL: { value: 'AMPL', text: '[AMPL] Ampleforth' },
  ANC: { value: 'ANC', text: '[ANC] Anchor Protocol' },
  ANCT: { value: 'ANCT', text: '[ANCT] Anchor' },
  ANKR: { value: 'ANKR', text: '[ANKR] Ankr' },
  ANT: { value: 'ANT', text: '[ANT] Aragon' },
  AOA: { value: 'AOA', text: '[AOA] Aurora' },
  APL: { value: 'APL', text: '[APL] Apollo Currency' },
  APM: { value: 'APM', text: '[APM] apM Coin' },
  AR: { value: 'AR', text: '[AR] Arweave' },
  ARDR: { value: 'ARDR', text: '[ARDR] Ardor' },
  ARK: { value: 'ARK', text: '[ARK] Ark' },
  ARPA: { value: 'ARPA', text: '[ARPA] ARPA Chain' },
  ARRR: { value: 'ARRR', text: '[ARRR] Pirate Chain' },
  AST: { value: 'AST', text: '[AST] AirSwap' },
  ATOM: { value: 'ATOM', text: '[ATOM] Cosmos' },
  ATT: { value: 'ATT', text: '[ATT] Attila' },
  AUDIO: { value: 'AUDIO', text: '[AUDIO] Audius' },
  AVA: { value: 'AVA', text: '[AVA] Travala.com' },
  AVAX: { value: 'AVAX', text: '[AVAX] Avalanche' },
  AXC: { value: 'AXC', text: '[AXC] AXIA Coin' },
  AXEL: { value: 'AXEL', text: '[AXEL] AXEL' },
  AXS: { value: 'AXS', text: '[AXS] Axie Infinity' },
  B2B: { value: 'B2B', text: '[B2B] B2BX' },
  BAL: { value: 'BAL', text: '[BAL] Balancer' },
  BAND: { value: 'BAND', text: '[BAND] Band Protocol' },
  BASIC: { value: 'BASIC', text: '[BASIC] BASIC' },
  BASID: { value: 'BASID', text: '[BASID] Basid Coin' },
  BAT: { value: 'BAT', text: '[BAT] Basic Attention Token' },
  BCD: { value: 'BCD', text: '[BCD] Bitcoin Diamond' },
  BCH: { value: 'BCH', text: '[BCH] Bitcoin Cash' },
  BCN: { value: 'BCN', text: '[BCN] Bytecoin' },
  BCZERO: { value: 'BCZERO', text: '[BCZERO] Buggyra Coin Zero' },
  BDCC: { value: 'BDCC', text: '[BDCC] BDCC Bitica COIN' },
  BDX: { value: 'BDX', text: '[BDX] Beldex' },
  BEAM: { value: 'BEAM', text: '[BEAM] Beam' },
  BEL: { value: 'BEL', text: '[BEL] Bella Protocol' },
  BHAO: { value: 'BHAO', text: '[BHAO] Bithao' },
  BHD: { value: 'BHD', text: '[BHD] BitcoinHD' },
  BHP: { value: 'BHP', text: '[BHP] BHPCoin' },
  BHT: { value: 'BHT', text: '[BHT] BHEX Token' },
  BICO: { value: 'BICO', text: '[BICO] Biconomy' },
  BIGONE: { value: 'BIGONE', text: '[BIGONE] BigONE Token' },
  BIKI: { value: 'BIKI', text: '[BIKI] BIKI' },
  BLCT: { value: 'BLCT', text: '[BLCT] Bloomzed Loyalty Club Ticket' },
  BLOCK: { value: 'BLOCK', text: '[BLOCK] Blocknet' },
  BLZ: { value: 'BLZ', text: '[BLZ] Bluzelle' },
  BNANA: { value: 'BNANA', text: '[BNANA] Chimpion' },
  BNB: { value: 'BNB', text: '[BNB] Binance Coin' },
  BNK: { value: 'BNK', text: '[BNK] Bankera' },
  BNT: { value: 'BNT', text: '[BNT] Bancor' },
  BOA: { value: 'BOA', text: '[BOA] BOSAGORA' },
  BONO: { value: 'BONO', text: '[BONO] Bonorum' },
  BORA: { value: 'BORA', text: '[BORA] BORA' },
  BOT: { value: 'BOT', text: '[BOT] Bounce Token' },
  BOTX: { value: 'BOTX', text: '[BOTX] botXcoin' },
  BPS: { value: 'BPS', text: '[BPS] BitcoinPoS' },
  BRC: { value: 'BRC', text: '[BRC] Baer Chain' },
  BRG: { value: 'BRG', text: '[BRG] Bridge Oracle' },
  BRZE: { value: 'BRZE', text: '[BRZE] Breezecoin' },
  BSV: { value: 'BSV', text: '[BSV] Bitcoin SV' },
  BTC: { value: 'BTC', text: '[BTC] Bitcoin' },
  BTC2: { value: 'BTC2', text: '[BTC2] Bitcoin 2' },
  BTCB: { value: 'BTCB', text: '[BTCB] Bitcoin BEP2' },
  BTG: { value: 'BTG', text: '[BTG] Bitcoin Gold' },
  BTM: { value: 'BTM', text: '[BTM] Bytom' },
  BTMX: { value: 'BTMX', text: '[BTMX] BitMax Token' },
  BTRS: { value: 'BTRS', text: '[BTRS] Bitball Treasure' },
  BTS: { value: 'BTS', text: '[BTS] BitShares' },
  BTT: { value: 'BTT', text: '[BTT] BitTorrent' },
  BTU: { value: 'BTU', text: '[BTU] BTU Protocol' },
  BUSD: { value: 'BUSD', text: '[BUSD] Binance USD' },
  BWF: { value: 'BWF', text: '[BWF] Beowulf' },
  BXK: { value: 'BXK', text: '[BXK] Bitbook Gambling' },
  BZ: { value: 'BZ', text: '[BZ] Bit-Z Token' },
  BZRX: { value: 'BZRX', text: '[BZRX] bZx Protocol' },
  C20: { value: 'C20', text: '[C20] CRYPTO20' },
  CADC: { value: 'CADC', text: '[CADC] CAD Coin' },
  CAKE: { value: 'CAKE', text: '[CAKE] PancakeSwap' },
  CCA: { value: 'CCA', text: '[CCA] Counos Coin' },
  CCXX: { value: 'CCXX', text: '[CCXX] Counos X' },
  CEL: { value: 'CEL', text: '[CEL] Celsius' },
  CELO: { value: 'CELO', text: '[CELO] Celo' },
  CELR: { value: 'CELR', text: '[CELR] Celer Network' },
  CENNZ: { value: 'CENNZ', text: '[CENNZ] Centrality' },
  CHR: { value: 'CHR', text: '[CHR] Chromia' },
  CHSB: { value: 'CHSB', text: '[CHSB] SwissBorg' },
  CHZ: { value: 'CHZ', text: '[CHZ] Chiliz' },
  CIPHC: { value: 'CIPHC', text: '[CIPHC] Cipher Core Token' },
  CIX100: { value: 'CIX100', text: '[CIX100] Cryptoindex.com 100' },
  CKB: { value: 'CKB', text: '[CKB] Nervos Network' },
  CND: { value: 'CND', text: '[CND] Cindicator' },
  CNX: { value: 'CNX', text: '[CNX] Cryptonex' },
  COCOS: { value: 'COCOS', text: '[COCOS] Cocos-BCX' },
  COMP: { value: 'COMP', text: '[COMP] Compound' },
  CON: { value: 'CON', text: '[CON] CONUN' },
  CORE: { value: 'CORE', text: '[CORE] cVault.finance' },
  COS: { value: 'COS', text: '[COS] Contentos' },
  COTI: { value: 'COTI', text: '[COTI] COTI' },
  CRE: { value: 'CRE', text: '[CRE] Carry' },
  CRO: { value: 'CRO', text: '[CRO] Crypto.com Coin' },
  CRPT: { value: 'CRPT', text: '[CRPT] Crypterium' },
  CRV: { value: 'CRV', text: '[CRV] Curve DAO Token' },
  CTC: { value: 'CTC', text: '[CTC] Creditcoin' },
  CTCN: { value: 'CTCN', text: '[CTCN] CONTRACOIN' },
  CTK: { value: 'CTK', text: '[CTK] CertiK' },
  CTSI: { value: 'CTSI', text: '[CTSI] Cartesi' },
  CTXC: { value: 'CTXC', text: '[CTXC] Cortex' },
  CUSD: { value: 'CUSD', text: '[CUSD] Celo Dollar' },
  CVA: { value: 'CVA', text: '[CVA] Crypto Village Accelerator' },
  CVC: { value: 'CVC', text: '[CVC] Civic' },
  CVNT: { value: 'CVNT', text: '[CVNT] Content Value Network' },
  CVT: { value: 'CVT', text: '[CVT] CyberVein' },
  CVX: { value: 'CVX', text: '[CVX] Convex Finance' },
  DAC: { value: 'DAC', text: '[DAC] Davinci Coin' },
  DAD: { value: 'DAD', text: '[DAD] DAD' },
  DAG: { value: 'DAG', text: '[DAG] Constellation' },
  DAI: { value: 'DAI', text: '[DAI] Dai' },
  DASH: { value: 'DASH', text: '[DASH] Dash' },
  DATA: { value: 'DATA', text: '[DATA] Streamr' },
  DCR: { value: 'DCR', text: '[DCR] Decred' },
  DCY: { value: 'DCY', text: '[DCY] Dinastycoin' },
  DEC: { value: 'DEC', text: '[DEC] Darico Ecosystem Coin' },
  DENT: { value: 'DENT', text: '[DENT] Dent' },
  DFI: { value: 'DFI', text: '[DFI] DeFiChain' },
  DGB: { value: 'DGB', text: '[DGB] DigiByte' },
  DGD: { value: 'DGD', text: '[DGD] DigixDAO' },
  DGTX: { value: 'DGTX', text: '[DGTX] Digitex Futures' },
  DIA: { value: 'DIA', text: '[DIA] DIA' },
  DIP: { value: 'DIP', text: '[DIP] Etherisc DIP Token' },
  DIVI: { value: 'DIVI', text: '[DIVI] Divi' },
  DMCH: { value: 'DMCH', text: '[DMCH] Darma Cash' },
  DMG: { value: 'DMG', text: '[DMG] DMM: Governance' },
  DNA: {
    value: 'DNA',
    text: '[DNA] Metaverse Dualchain Network Architecture',
  },
  DNT: { value: 'DNT', text: '[DNT] district0x' },
  DOGE: { value: 'DOGE', text: '[DOGE] Dogecoin' },
  DOT: { value: 'DOT', text: '[DOT] Polkadot' },
  DREP: { value: 'DREP', text: '[DREP] DREP' },
  DRGN: { value: 'DRGN', text: '[DRGN] Dragonchain' },
  DRS: { value: 'DRS', text: '[DRS] Doctors Coin' },
  DTR: { value: 'DTR', text: '[DTR] Dynamic Trading Rights' },
  DUSK: { value: 'DUSK', text: '[DUSK] Dusk Network' },
  DX: { value: 'DX', text: '[DX] DxChain Token' },
  ECOREAL: { value: 'ECOREAL', text: '[ECOREAL] Ecoreal Estate' },
  EDC: { value: 'EDC', text: '[EDC] EDC Blockchain v1 [old]' },
  EGLD: { value: 'EGLD', text: '[EGLD] Elrond' },
  ELA: { value: 'ELA', text: '[ELA] Elastos' },
  ELF: { value: 'ELF', text: '[ELF] aelf' },
  ELON: { value: 'ELON', text: '[ELON] Dogelon Mars' },
  EMC2: { value: 'EMC2', text: '[EMC2] Einsteinium' },
  ENG: { value: 'ENG', text: '[ENG] Enigma' },
  ENJ: { value: 'ENJ', text: '[ENJ] Enjin Coin' },
  ENS: { value: 'ENS', text: '[ENS] Ethereum Name Service' },
  EOS: { value: 'EOS', text: '[EOS] EOS' },
  ERC20: { value: 'ERC20', text: '[ERC20] ERC20' },
  ERG: { value: 'ERG', text: '[ERG] Ergo' },
  ETC: { value: 'ETC', text: '[ETC] Ethereum Classic' },
  ETH: { value: 'ETH', text: '[ETH] Ethereum' },
  ETN: { value: 'ETN', text: '[ETN] Electroneum' },
  EUM: { value: 'EUM', text: '[EUM] Elitium' },
  EURS: { value: 'EURS', text: '[EURS] STASIS EURO' },
  EVN: { value: 'EVN', text: '[EVN] Envion' },
  EVR: { value: 'EVR', text: '[EVR] Everus' },
  EWT: { value: 'EWT', text: '[EWT] Energy Web Token' },
  FAB: { value: 'FAB', text: '[FAB] FABRK' },
  FARM: { value: 'FARM', text: '[FARM] Harvest Finance' },
  FET: { value: 'FET', text: '[FET] Fetch.ai' },
  FIL: { value: 'FIL', text: '[FIL] Filecoin' },
  FLG: { value: 'FLG', text: '[FLG] Folgory Coin' },
  FLM: { value: 'FLM', text: '[FLM] Flamingo' },
  FLOW: { value: 'FLOW', text: '[FLOW] Flow' },
  FNB: { value: 'FNB', text: '[FNB] FNB Protocol' },
  FSN: { value: 'FSN', text: '[FSN] Fusion' },
  FST: { value: 'FST', text: '[FST] 1irstcoin' },
  FTM: { value: 'FTM', text: '[FTM] Fantom' },
  FTT: { value: 'FTT', text: '[FTT] FTX Token' },
  FUN: { value: 'FUN', text: '[FUN] FunFair' },
  FX: { value: 'FX', text: '[FX] Function X' },
  FXC: { value: 'FXC', text: '[FXC] Flexacoin' },
  GALA: { value: 'GALA', text: '[GALA] Gala' },
  GARD: { value: 'GARD', text: '[GARD] Hashgard' },
  GAS: { value: 'GAS', text: '[GAS] Gas' },
  GBYTE: { value: 'GBYTE', text: '[GBYTE] Obyte' },
  GLEEC: { value: 'GLEEC', text: '[GLEEC] Gleec' },
  GNO: { value: 'GNO', text: '[GNO] Gnosis' },
  GNT: { value: 'GNT', text: '[GNT] Golem' },
  GRIN: { value: 'GRIN', text: '[GRIN] Grin' },
  GRN: { value: 'GRN', text: '[GRN] GreenPower' },
  GRS: { value: 'GRS', text: '[GRS] Groestlcoin' },
  GRT: { value: 'GRT', text: '[GRT] The Graph' },
  GT: { value: 'GT', text: '[GT] GateToken' },
  GUSD: { value: 'GUSD', text: '[GUSD] Gemini Dollar' },
  GXC: { value: 'GXC', text: '[GXC] GXChain' },
  HBAR: { value: 'HBAR', text: '[HBAR] Hedera Hashgraph' },
  HBTC: { value: 'HBTC', text: '[HBTC] Huobi BTC' },
  HC: { value: 'HC', text: '[HC] HyperCash' },
  HEDG: { value: 'HEDG', text: '[HEDG] HedgeTrade' },
  HEX: { value: 'HEX', text: '[HEX] HEX' },
  HIVE: { value: 'HIVE', text: '[HIVE] Hive' },
  HMR: { value: 'HMR', text: '[HMR] Homeros' },
  HNC: { value: 'HNC', text: '[HNC] Hellenic Coin' },
  HNS: { value: 'HNS', text: '[HNS] Handshake' },
  HNT: { value: 'HNT', text: '[HNT] Helium' },
  HOD: { value: 'HOD', text: '[HOD] HoDooi.com' },
  HOT: { value: 'HOT', text: '[HOT] Holo' },
  HPT: { value: 'HPT', text: '[HPT] Huobi Pool Token' },
  HSN: { value: 'HSN', text: '[HSN] Helper Search Token' },
  HT: { value: 'HT', text: '[HT] Huobi Token' },
  HUSD: { value: 'HUSD', text: '[HUSD] HUSD' },
  HXRO: { value: 'HXRO', text: '[HXRO] Hxro' },
  HYN: { value: 'HYN', text: '[HYN] Hyperion' },
  ICH: { value: 'ICH', text: '[ICH] Idea Chain Coin' },
  ICP: { value: 'ICP', text: '[ICP] Internet Computer' },
  ICX: { value: 'ICX', text: '[ICX] ICON' },
  IDEX: { value: 'IDEX', text: '[IDEX] IDEX' },
  IGNIS: { value: 'IGNIS', text: '[IGNIS] Ignis' },
  IHF: { value: 'IHF', text: '[IHF] Invictus Hyperion Fund' },
  ILV: { value: 'ILV', text: '[ILV] Illuvium' },
  IMX: { value: 'IMX', text: '[IMX] Immutable X' },
  INB: { value: 'INB', text: '[INB] Insight Chain' },
  INJ: { value: 'INJ', text: '[INJ] Injective Protocol' },
  INO: { value: 'INO', text: '[INO] INO COIN' },
  INSTAR: { value: 'INSTAR', text: '[INSTAR] Insights Network' },
  IOST: { value: 'IOST', text: '[IOST] IOST' },
  IOTX: { value: 'IOTX', text: '[IOTX] IoTeX' },
  IPX: { value: 'IPX', text: '[IPX] Tachyon Protocol' },
  IQ: { value: 'IQ', text: '[IQ] Everipedia' },
  IRIS: { value: 'IRIS', text: '[IRIS] IRISnet' },
  IZE: { value: 'IZE', text: '[IZE] IZE' },
  JST: { value: 'JST', text: '[JST] JUST' },
  JUL: { value: 'JUL', text: '[JUL] Joule' },
  JWL: { value: 'JWL', text: '[JWL] Jewel' },
  KAI: { value: 'KAI', text: '[KAI] KardiaChain' },
  KAN: { value: 'KAN', text: '[KAN] BitKan' },
  KAVA: { value: 'KAVA', text: '[KAVA] Kava.io' },
  KBC: { value: 'KBC', text: '[KBC] Karatgold Coin' },
  KCASH: { value: 'KCASH', text: '[KCASH] Kcash' },
  KCS: { value: 'KCS', text: '[KCS] KuCoin Shares' },
  KDA: { value: 'KDA', text: '[KDA] Kadena' },
  KDAG: { value: 'KDAG', text: '[KDAG] King DAG' },
  KEEP: { value: 'KEEP', text: '[KEEP] Keep Network' },
  KIN: { value: 'KIN', text: '[KIN] Kin' },
  KLAY: { value: 'KLAY', text: '[KLAY] Klaytn' },
  KMD: { value: 'KMD', text: '[KMD] Komodo' },
  KNC: { value: 'KNC', text: '[KNC] Kyber Network' },
  KP3R: { value: 'KP3R', text: '[KP3R] Keep3rV1' },
  KRT: { value: 'KRT', text: '[KRT] TerraKRW' },
  KSM: { value: 'KSM', text: '[KSM] Kusama' },
  LA: { value: 'LA', text: '[LA] LATOKEN' },
  LAMB: { value: 'LAMB', text: '[LAMB] Lambda' },
  LBC: { value: 'LBC', text: '[LBC] LBRY Credits' },
  LCX: { value: 'LCX', text: '[LCX] LCX' },
  LEO: { value: 'LEO', text: '[LEO] UNUS SED LEO' },
  LEVL: { value: 'LEVL', text: '[LEVL] Levolution' },
  LINK: { value: 'LINK', text: '[LINK] Chainlink' },
  LOKI: { value: 'LOKI', text: '[LOKI] Loki' },
  LOOM: { value: 'LOOM', text: '[LOOM] Loom Network' },
  LPT: { value: 'LPT', text: '[LPT] Livepeer' },
  LRC: { value: 'LRC', text: '[LRC] Loopring' },
  LRG: { value: 'LRG', text: '[LRG] Largo Coin' },
  LSK: { value: 'LSK', text: '[LSK] Lisk' },
  LTC: { value: 'LTC', text: '[LTC] Litecoin' },
  LTO: { value: 'LTO', text: '[LTO] LTO Network' },
  LUNA: { value: 'LUNA', text: '[LUNA] Terra' },
  LVX: { value: 'LVX', text: '[LVX] Level01' },
  MAID: { value: 'MAID', text: '[MAID] MaidSafeCoin' },
  MANA: { value: 'MANA', text: '[MANA] Decentraland' },
  MASS: { value: 'MASS', text: '[MASS] Massnet' },
  MATH: { value: 'MATH', text: '[MATH] MATH' },
  MATIC: { value: 'MATIC', text: '[MATIC] Matic Network' },
  MBL: { value: 'MBL', text: '[MBL] MovieBloc' },
  MBN: { value: 'MBN', text: '[MBN] Mobilian Coin' },
  MCO: { value: 'MCO', text: '[MCO] MCO' },
  MDA: { value: 'MDA', text: '[MDA] Moeda Loyalty Points' },
  MED: { value: 'MED', text: '[MED] MediBloc' },
  MFT: { value: 'MFT', text: '[MFT] Mainframe' },
  MIN: { value: 'MIN', text: '[MIN] MINDOL' },
  MIOTA: { value: 'MIOTA', text: '[MIOTA] IOTA' },
  MKR: { value: 'MKR', text: '[MKR] Maker' },
  MLK: { value: 'MLK', text: '[MLK] MiL.k' },
  MLN: { value: 'MLN', text: '[MLN] Melon' },
  MOF: { value: 'MOF', text: '[MOF] Molecular Future' },
  MONA: { value: 'MONA', text: '[MONA] MonaCoin' },
  MRPH: { value: 'MRPH', text: '[MRPH] Morpheus.Network' },
  MTA: { value: 'MTA', text: '[MTA] Meta' },
  MTC: { value: 'MTC', text: '[MTC] Metacoin' },
  MTL: { value: 'MTL', text: '[MTL] Metal' },
  MTXLT: { value: 'MTXLT', text: '[MTXLT] Tixl' },
  MUSD: { value: 'MUSD', text: '[MUSD] mStable USD' },
  MVL: { value: 'MVL', text: '[MVL] MVL' },
  MWC: { value: 'MWC', text: '[MWC] MimbleWimbleCoin' },
  MX: { value: 'MX', text: '[MX] MX Token' },
  MXC: { value: 'MXC', text: '[MXC] MXC' },
  NANO: { value: 'NANO', text: '[NANO] Nano' },
  NAS: { value: 'NAS', text: '[NAS] Nebulas' },
  NEAR: { value: 'NEAR', text: '[NEAR] NEAR Protocol' },
  NEC: { value: 'NEC', text: '[NEC] Nectar' },
  NEO: { value: 'NEO', text: '[NEO] Neo' },
  NEST: { value: 'NEST', text: '[NEST] NEST Protocol' },
  NEX: { value: 'NEX', text: '[NEX] Nash Exchange' },
  NEXO: { value: 'NEXO', text: '[NEXO] Nexo' },
  NEXXO: { value: 'NEXXO', text: '[NEXXO] Nexxo' },
  NIM: { value: 'NIM', text: '[NIM] Nimiq' },
  NKN: { value: 'NKN', text: '[NKN] NKN' },
  NMR: { value: 'NMR', text: '[NMR] Numeraire' },
  NOIA: { value: 'NOIA', text: '[NOIA] NOIA Network' },
  NPXS: { value: 'NPXS', text: '[NPXS] Pundi X' },
  NRG: { value: 'NRG', text: '[NRG] Energi' },
  NU: { value: 'NU', text: '[NU] NuCypher' },
  NULS: { value: 'NULS', text: '[NULS] NULS' },
  NUT: { value: 'NUT', text: '[NUT] Native Utility Token' },
  NVT: { value: 'NVT', text: '[NVT] NerveNetwork' },
  NWC: { value: 'NWC', text: '[NWC] Newscrypto' },
  NXM: { value: 'NXM', text: '[NXM] NXM' },
  NXS: { value: 'NXS', text: '[NXS] Nexus' },
  NYE: { value: 'NYE', text: '[NYE] NewYork Exchange' },
  OCEAN: { value: 'OCEAN', text: '[OCEAN] Ocean Protocol' },
  OCTO: { value: 'OCTO', text: '[OCTO] OctoFi' },
  OGN: { value: 'OGN', text: '[OGN] Origin Protocol' },
  OKB: { value: 'OKB', text: '[OKB] OKB' },
  OMG: { value: 'OMG', text: '[OMG] OMG Network' },
  ONE: { value: 'ONE', text: '[ONE] Harmony' },
  ONOT: { value: 'ONOT', text: '[ONOT] ONOToken' },
  ONT: { value: 'ONT', text: '[ONT] Ontology' },
  ORBS: { value: 'ORBS', text: '[ORBS] Orbs' },
  ORC: { value: 'ORC', text: '[ORC] Orbit Chain' },
  ORN: { value: 'ORN', text: '[ORN] Orion Protocol' },
  OXT: { value: 'OXT', text: '[OXT] Orchid' },
  PAI: { value: 'PAI', text: '[PAI] Project Pai' },
  PAX: { value: 'PAX', text: '[PAX] Paxos Standard' },
  PAXG: { value: 'PAXG', text: '[PAXG] PAX Gold' },
  PCN: { value: 'PCN', text: '[PCN] PeepCoin' },
  PCX: { value: 'PCX', text: '[PCX] ChainX' },
  PERL: { value: 'PERL', text: '[PERL] Perlin' },
  PERP: { value: 'PERP', text: '[PERP] Perpetual Protocol' },
  PHA: { value: 'PHA', text: '[PHA] Phala.Network' },
  PIVX: { value: 'PIVX', text: '[PIVX] PIVX' },
  PLC: { value: 'PLC', text: '[PLC] PLATINCOIN' },
  PLF: { value: 'PLF', text: '[PLF] PlayFuel' },
  PNK: { value: 'PNK', text: '[PNK] Kleros' },
  POLS: { value: 'POLS', text: '[POLS] Polkastarter' },
  POLY: { value: 'POLY', text: '[POLY] Polymath' },
  POWR: { value: 'POWR', text: '[POWR] Power Ledger' },
  PPT: { value: 'PPT', text: '[PPT] Populous' },
  PROM: { value: 'PROM', text: '[PROM] Prometeus' },
  PRQ: { value: 'PRQ', text: '[PRQ] PARSIQ' },
  PZM: { value: 'PZM', text: '[PZM] PRIZM' },
  QASH: { value: 'QASH', text: '[QASH] QASH' },
  QC: { value: 'QC', text: '[QC] Qcash' },
  QKC: { value: 'QKC', text: '[QKC] QuarkChain' },
  QNT: { value: 'QNT', text: '[QNT] Quant' },
  QQQ: { value: 'QQQ', text: '[QQQ] Poseidon Network' },
  QRK: { value: 'QRK', text: '[QRK] Quark' },
  QRL: { value: 'QRL', text: '[QRL] Quantum Resistant Ledger' },
  QSP: { value: 'QSP', text: '[QSP] Quantstamp' },
  QTUM: { value: 'QTUM', text: '[QTUM] Qtum' },
  RCHAINREV: { value: 'RCHAINREV', text: '[RCHAINREV] RChain' },
  RCN: { value: 'RCN', text: '[RCN] Ripio Credit Network' },
  RDD: { value: 'RDD', text: '[RDD] ReddCoin' },
  RDN: { value: 'RDN', text: '[RDN] Raiden Network Token' },
  REN: { value: 'REN', text: '[REN] Ren' },
  RENBTC: { value: 'RENBTC', text: '[RENBTC] renBTC' },
  REP: { value: 'REP', text: '[REP] Augur' },
  REPO: { value: 'REPO', text: '[REPO] REPO' },
  REQ: { value: 'REQ', text: '[REQ] Request' },
  REV: { value: 'REV', text: '[REV] Revain' },
  RIF: { value: 'RIF', text: '[RIF] RSK Infrastructure Framework' },
  RING: { value: 'RING', text: '[RING] Darwinia Network' },
  RKN: { value: 'RKN', text: '[RKN] Rakon' },
  RLC: { value: 'RLC', text: '[RLC] iExec RLC' },
  RNDR: { value: 'RNDR', text: '[RNDR] Render Token' },
  ROSE: { value: 'ROSE', text: '[ROSE] Oasis Network' },
  RPL: { value: 'RPL', text: '[RPL] Rocket Pool' },
  RSR: { value: 'RSR', text: '[RSR] Reserve Rights' },
  RUNE: { value: 'RUNE', text: '[RUNE] THORChain' },
  RVN: { value: 'RVN', text: '[RVN] Ravencoin' },
  S4F: { value: 'S4F', text: '[S4F] S4FE' },
  SAFE: { value: 'SAFE', text: '[SAFE] yieldfarming.insure' },
  SAND: { value: 'SAND', text: '[SAND] The Sandbox' },
  SAPP: { value: 'SAPP', text: '[SAPP] Sapphire' },
  SC: { value: 'SC', text: '[SC] Siacoin' },
  SCC: { value: 'SCC', text: '[SCC] STEM CELL COIN' },
  SCRT: { value: 'SCRT', text: '[SCRT] Secret' },
  SEELE: { value: 'SEELE', text: '[SEELE] Seele-N' },
  SERO: { value: 'SERO', text: '[SERO] Super Zero Protocol' },
  SFP: { value: 'SFP', text: '[SFP] SafePal' },
  SHIB: { value: 'SHIB', text: '[SHIB] Shiba Inu' },
  SHPING: { value: 'SHPING', text: '[SHPING] SHPING' },
  SHR: { value: 'SHR', text: '[SHR] ShareToken' },
  SLS: { value: 'SLS', text: '[SLS] SaluS' },
  SNB: { value: 'SNB', text: '[SNB] SynchroBitcoin' },
  SNL: { value: 'SNL', text: '[SNL] Sport and Leisure' },
  SNT: { value: 'SNT', text: '[SNT] Status' },
  SNTVT: { value: 'SNTVT', text: '[SNTVT] Sentivate' },
  SNX: { value: 'SNX', text: '[SNX] Synthetix' },
  SOL: { value: 'SOL', text: '[SOL] Solana' },
  SOLO: { value: 'SOLO', text: '[SOLO] Sologenic' },
  SOLVE: { value: 'SOLVE', text: '[SOLVE] SOLVE' },
  SPND: { value: 'SPND', text: '[SPND] Spendcoin' },
  SRM: { value: 'SRM', text: '[SRM] Serum' },
  STAKE: { value: 'STAKE', text: '[STAKE] xDai' },
  STEEM: { value: 'STEEM', text: '[STEEM] Steem' },
  STMX: { value: 'STMX', text: '[STMX] StormX' },
  STORJ: { value: 'STORJ', text: '[STORJ] Storj' },
  STP: { value: 'STP', text: '[STP] STPAY' },
  STPT: { value: 'STPT', text: '[STPT] Standard Tokenization Protocol' },
  STRAT: { value: 'STRAT', text: '[STRAT] Stratis' },
  STRONG: { value: 'STRONG', text: '[STRONG] Strong' },
  STX: { value: 'STX', text: '[STX] Blockstack' },
  SUKU: { value: 'SUKU', text: '[SUKU] SUKU' },
  SUN: { value: 'SUN', text: '[SUN] SUN' },
  SUSD: { value: 'SUSD', text: '[SUSD] sUSD' },
  SUSHI: { value: 'SUSHI', text: '[SUSHI] SushiSwap' },
  SWAP: { value: 'SWAP', text: '[SWAP] TrustSwap' },
  SWINGBY: { value: 'SWINGBY', text: '[SWINGBY] Swingby' },
  SWTH: { value: 'SWTH', text: '[SWTH] Switcheo' },
  SXP: { value: 'SXP', text: '[SXP] Swipe' },
  SYS: { value: 'SYS', text: '[SYS] Syscoin' },
  TCAD: { value: 'TCAD', text: '[TCAD] TrueCAD' },
  TEL: { value: 'TEL', text: '[TEL] Telcoin' },
  TFUEL: { value: 'TFUEL', text: '[TFUEL] Theta Fuel' },
  THETA: { value: 'THETA', text: '[THETA] THETA' },
  THR: { value: 'THR', text: '[THR] ThoreCoin' },
  THX: { value: 'THX', text: '[THX] ThoreNext' },
  TITAN: { value: 'TITAN', text: '[TITAN] TitanSwap' },
  TMTG: { value: 'TMTG', text: '[TMTG] The Midas Touch Gold' },
  TNC: { value: 'TNC', text: '[TNC] TNC Coin' },
  TOMO: { value: 'TOMO', text: '[TOMO] TomoChain' },
  TONIC: { value: 'TONIC', text: '[TONIC] Tectonic' },
  TRAC: { value: 'TRAC', text: '[TRAC] OriginTrail' },
  TRAT: { value: 'TRAT', text: '[TRAT] Tratin' },
  TRB: { value: 'TRB', text: '[TRB] Tellor' },
  TROY: { value: 'TROY', text: '[TROY] TROY' },
  TRUE: { value: 'TRUE', text: '[TRUE] TrueChain' },
  TRX: { value: 'TRX', text: '[TRX] TRON' },
  TSHP: { value: 'TSHP', text: '[TSHP] 12Ships' },
  TT: { value: 'TT', text: '[TT] Thunder Token' },
  TTT: { value: 'TTT', text: '[TTT] The Transfer Token' },
  TUSD: { value: 'TUSD', text: '[TUSD] TrueUSD' },
  TWT: { value: 'TWT', text: '[TWT] Trust Wallet Token' },
  UBT: { value: 'UBT', text: '[UBT] Unibright' },
  ULT: { value: 'ULT', text: '[ULT] Ultiledger' },
  UMA: { value: 'UMA', text: '[UMA] UMA' },
  UNI: { value: 'UNI', text: '[UNI] Uniswap' },
  UNICOIN: { value: 'UNICOIN', text: '[UNICOIN] UNI COIN' },
  UNO: { value: 'UNO', text: '[UNO] Unobtanium' },
  UOS: { value: 'UOS', text: '[UOS] Ultra' },
  UQC: { value: 'UQC', text: '[UQC] Uquid Coin' },
  USDC: { value: 'USDC', text: '[USDC] USD Coin' },
  USDJ: { value: 'USDJ', text: '[USDJ] USDJ' },
  USDK: { value: 'USDK', text: '[USDK] USDK' },
  USDN: { value: 'USDN', text: '[USDN] Neutrino USD' },
  USDP: { value: 'USDP', text: '[USDP] Pax Dollar' },
  USDT: { value: 'USDT', text: '[USDT] Tether' },
  UST: { value: 'UST', text: '[UST] TerraUSD' },
  UTK: { value: 'UTK', text: '[UTK] Utrust' },
  VEST: { value: 'VEST', text: '[VEST] VestChain' },
  VET: { value: 'VET', text: '[VET] VeChain' },
  VGX: { value: 'VGX', text: '[VGX] Voyager Token' },
  VIDT: { value: 'VIDT', text: '[VIDT] VIDT Datalink' },
  VITAE: { value: 'VITAE', text: '[VITAE] Vitae' },
  VLX: { value: 'VLX', text: '[VLX] Velas' },
  VRSC: { value: 'VRSC', text: '[VRSC] VerusCoin' },
  VSYS: { value: 'VSYS', text: '[VSYS] v.systems' },
  VTHO: { value: 'VTHO', text: '[VTHO] VeThor Token' },
  VVS: { value: 'VVS', text: '[VVS] VVS Finance' },
  WAN: { value: 'WAN', text: '[WAN] Wanchain' },
  WAVES: { value: 'WAVES', text: '[WAVES] Waves' },
  WAXP: { value: 'WAXP', text: '[WAXP] WAX' },
  WBNB: { value: 'WBNB', text: '[WBNB] Wrapped BNB' },
  WBTC: { value: 'WBTC', text: '[WBTC] Wrapped Bitcoin' },
  WET: { value: 'WET', text: '[WET] WeShow Token' },
  WICC: { value: 'WICC', text: '[WICC] WaykiChain' },
  WIN: { value: 'WIN', text: '[WIN] WINk' },
  WIX: { value: 'WIX', text: '[WIX] Wixlar' },
  WOM: { value: 'WOM', text: '[WOM] WOM Protocol' },
  WRX: { value: 'WRX', text: '[WRX] WazirX' },
  WTC: { value: 'WTC', text: '[WTC] Waltonchain' },
  WXT: { value: 'WXT', text: '[WXT] Wirex Token' },
  XCM: { value: 'XCM', text: '[XCM] CoinMetro Token' },
  XDC: { value: 'XDC', text: '[XDC] XinFin Network' },
  XEC: { value: 'XEC', text: '[XEC] eCash' },
  XEM: { value: 'XEM', text: '[XEM] NEM' },
  XHV: { value: 'XHV', text: '[XHV] Haven Protocol' },
  XIN: { value: 'XIN', text: '[XIN] Mixin' },
  XLM: { value: 'XLM', text: '[XLM] Stellar' },
  XLT: { value: 'XLT', text: '[XLT] Nexalt' },
  XMR: { value: 'XMR', text: '[XMR] Monero' },
  XNC: { value: 'XNC', text: '[XNC] XeniosCoin' },
  XOR: { value: 'XOR', text: '[XOR] Sora' },
  XPR: { value: 'XPR', text: '[XPR] Proton' },
  XRP: { value: 'XRP', text: '[XRP] XRP' },
  XSN: { value: 'XSN', text: '[XSN] Stakenet' },
  XSR: { value: 'XSR', text: '[XSR] Xensor' },
  XT: { value: 'XT', text: '[XT] ExtStock Token' },
  XTP: { value: 'XTP', text: '[XTP] Tap' },
  XTZ: { value: 'XTZ', text: '[XTZ] Tezos' },
  XVG: { value: 'XVG', text: '[XVG] Verge' },
  XVS: { value: 'XVS', text: '[XVS] Venus' },
  XWC: { value: 'XWC', text: '[XWC] WhiteCoin' },
  XYM: { value: 'XYM', text: '[XYM] Symbol' },
  XYO: { value: 'XYO', text: '[XYO] XYO' },
  XZC: { value: 'XZC', text: '[XZC] Zcoin' },
  YEP: { value: 'YEP', text: '[YEP] YEP COIN' },
  'YF-DAI': { value: 'YF-DAI', text: '[YF-DAI] YFDAI.FINANCE' },
  YFI: { value: 'YFI', text: '[YFI] yearn.finance' },
  YFII: { value: 'YFII', text: '[YFII] DFI.Money' },
  YFL: { value: 'YFL', text: '[YFL] YF Link' },
  YGG: { value: 'YGG', text: '[YGG] Yield Guild Games' },
  YUSRA: { value: 'YUSRA', text: '[YUSRA] YUSRA' },
  ZAP: { value: 'ZAP', text: '[ZAP] Zap' },
  ZB: { value: 'ZB', text: '[ZB] ZB Token' },
  ZCN: { value: 'ZCN', text: '[ZCN] 0Chain' },
  ZEC: { value: 'ZEC', text: '[ZEC] Zcash' },
  ZEN: { value: 'ZEN', text: '[ZEN] Horizen' },
  ZIL: { value: 'ZIL', text: '[ZIL] Zilliqa' },
  ZLW: { value: 'ZLW', text: '[ZLW] Zelwin' },
  ZNN: { value: 'ZNN', text: '[ZNN] Zenon' },
  ZRX: { value: 'ZRX', text: '[ZRX] 0x' },
  ZT: { value: 'ZT', text: '[ZT] ZBG Token' },
  ZYN: { value: 'ZYN', text: '[ZYN] Zynecoin' },
  OTHER: { value: 'OTH', text: 'Other' },
} as const satisfies Record<string, U21SelectOptionProps<string>>;

export const FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS = generateOptions(
  FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE,
);

export const FINTRAC_STR_ACCOUNT_TYPE_CODE = {
  PERSONAL: {
    value: 1,
    text: 'Personal',
  },
  BUSINESS: {
    value: 2,
    text: 'Business',
  },
  TRUST: {
    value: 3,
    text: 'Trust',
  },
  CASINO: {
    value: 5,
    text: 'Casino',
  },
  OTHER: {
    value: 4,
    text: 'Other',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_ACCOUNT_TYPE_CODE_OPTIONS = generateOptions(
  FINTRAC_STR_ACCOUNT_TYPE_CODE,
);

export const FINTRAC_STR_ACCOUNT_STATUS_AT_TIME_OF_TRANSACTION = {
  ACTIVE: {
    value: 1,
    text: 'Active',
  },
  INACTIVE: {
    value: 2,
    text: 'Inactive',
  },
  DORMANT: {
    value: 3,
    text: 'Dormant',
  },
  CLOSED: {
    value: 4,
    text: 'Closed',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_ACCOUNT_STATUS_AT_TIME_OF_TRANSACTION_OPTIONS =
  generateOptions(FINTRAC_STR_ACCOUNT_STATUS_AT_TIME_OF_TRANSACTION);

export const FINTRAC_STR_DEVICE_CODE = {
  COMPUTER_LAPTOP: {
    value: 1,
    text: 'Computer/Laptop',
  },
  MOBILE_PHONE: {
    value: 2,
    text: 'Mobile phone',
  },
  TABLET: {
    value: 3,
    text: 'Tablet',
  },
  OTHER: {
    value: 4,
    text: 'Other',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_DEVICE_CODE_OPTIONS = generateOptions(
  FINTRAC_STR_DEVICE_CODE,
);

export const FINTRAC_STR_RELATIONSHIP_OF_CONDUCTOR_CODE = {
  ACCOUNTANT: {
    value: 1,
    text: 'Accountant',
  },
  AGENT: {
    value: 2,
    text: 'Agent',
  },
  BORROWER: {
    value: 3,
    text: 'Borrower',
  },
  BROKER: {
    value: 4,
    text: 'Broker',
  },
  CUSTOMER: {
    value: 5,
    text: 'Customer',
  },
  EMPLOYEE: {
    value: 6,
    text: 'Employee',
  },
  FRIEND: {
    value: 7,
    text: 'Friend',
  },
  RELATIVE: {
    value: 8,
    text: 'Relative',
  },
  OTHER: {
    value: 9,
    text: 'Other',
  },
  LEGAL_COUNSEL: {
    value: 10,
    text: 'Legal counsel',
  },
  EMPLOYER: {
    value: 11,
    text: 'Employer',
  },
  JOINT_SECONDARY_OWNER: {
    value: 12,
    text: 'Joint/Secondary owner',
  },
  POWER_OF_ATTORNEY: {
    value: 13,
    text: 'Power of attorney',
  },
  VENDOR_SUPPLIER: {
    value: 14,
    text: 'Vendor/Supplier',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_RELATIONSHIP_OF_CONDUCTOR_CODE_OPTIONS =
  generateOptions(FINTRAC_STR_RELATIONSHIP_OF_CONDUCTOR_CODE);

export const FINTRAC_STR_DISPOSITION_CODE = {
  DEPOSIT_TO_ACCOUNT: {
    value: 1,
    text: 'Deposit to account',
  },
  EXCHANGE_TO_FIAT_CURRENCY: {
    value: 3,
    text: 'Exchange to fiat currency',
  },
  PURCHASE_OF_CASINO_PRODUCT: {
    value: 4,
    text: 'Purchase of casino product',
  },
  PURCHASE_OF_BANK_DRAFT: {
    value: 5,
    text: 'Purchase of bank draft',
  },
  PURCHASE_OF_MONEY_ORDER: {
    value: 6,
    text: 'Purchase of money order',
  },
  LIFE_INSURANCE_POLICY_PURCHASE_DEPOSIT: {
    value: 7,
    text: 'Life insurance policy purchase/deposit',
  },
  INVESTMENT_PRODUCT_PURCHASE_DEPOSIT: {
    value: 8,
    text: 'Investment product purchase/deposit',
  },
  REAL_ESTATE_PURCHASE_DEPOSIT: {
    value: 9,
    text: 'Real estate purchase/deposit',
  },
  CASH_OUT: {
    value: 10,
    text: 'Cash out',
  },
  PURCHASE_OF_JEWELERY: {
    value: 14,
    text: 'Purchase of jewelery',
  },
  PURCHASE_OF_PRECIOUS_METALS: {
    value: 15,
    text: 'Purchase of precious metals',
  },
  ADDED_TO_VIRTUAL_CURRENCY_WALLET: {
    value: 17,
    text: 'Added to virtual currency wallet',
  },
  EXCHANGE_TO_VIRTUAL_CURRENCY: {
    value: 18,
    text: 'Exchange to virtual currency',
  },
  OUTGOING_VIRTUAL_CURRENCY_TRANSFER: {
    value: 19,
    text: 'Outgoing virtual currency transfer',
  },
  OUTGOING_EMAIL_MONEY_TRANSFER: {
    value: 20,
    text: 'Outgoing email money transfer',
  },
  HOLDING_FUNDS: {
    value: 21,
    text: 'Holding funds',
  },
  PURCHASE_OF_PRECIOUS_STONES: {
    value: 22,
    text: 'Purchase of precious stones',
  },
  ISSUED_CHEQUE: {
    value: 23,
    text: 'Issued cheque',
  },
  OUTGOING_DOMESTIC_FUNDS_TRANSFER: {
    value: 24,
    text: 'Outgoing domestic funds transfer',
  },
  OUTGOING_INTERNATIONAL_FUNDS_TRANSFER: {
    value: 25,
    text: 'Outgoing international funds transfer',
  },
  PURCHASE_OF_PREPAID_PAYMENT_PRODUCT_CARD: {
    value: 26,
    text: 'Purchase of prepaid payment product/card',
  },
  DENOMINATION_EXCHANGE: {
    value: 27,
    text: 'Denomination exchange',
  },
  PAYMENT_TO_ACCOUNT: {
    value: 28,
    text: 'Payment to account',
  },
  PURCHASE_OF_PAYMENT_FOR_GOODS: {
    value: 29,
    text: 'Purchase of / Payment for goods',
  },
  PURCHASE_OF_PAYMENT_FOR_SERVICES: {
    value: 30,
    text: 'Purchase of / Payment for services',
  },
  OUTGOING_MOBILE_MONEY_TRANSFER: {
    value: 31,
    text: 'Outgoing mobile money transfer',
  },
  CASH_WITHDRAWAL_ACCOUNT_BASED: {
    value: 32,
    text: 'Cash withdrawal (account based)',
  },
  OTHER: {
    value: 11,
    text: 'Other',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_DISPOSITION_CODE_OPTIONS = generateOptions<number>(
  FINTRAC_STR_DISPOSITION_CODE,
);

export const FINTRAC_STR_ADDRESS_TYPE_CODE = {
  STRUCTURED_ADDRESS: {
    value: 1,
    text: 'Structured address',
  },
  UNSTRUCTURED_ADDRESS: {
    value: 2,
    text: 'Unstructured address',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_ADDRESS_TYPE_CODE_OPTIONS = generateOptions<number>(
  FINTRAC_STR_ADDRESS_TYPE_CODE,
);

export const FINTRAC_STR_COUNTRY_CODE = {
  CANADA: {
    value: 'CA',
    text: 'Canada',
  },
  UNITED_STATES: {
    value: 'US',
    text: 'United States',
  },
  MEXICO: {
    value: 'MX',
    text: 'Mexico',
  },
  ANDORRA: {
    value: 'AD',
    text: 'Andorra',
  },
  UNITED_ARAB_EMIRATES: {
    value: 'AE',
    text: 'United Arab Emirates',
  },
  AFGHANISTAN: {
    value: 'AF',
    text: 'Afghanistan',
  },
  ANTIGUA_AND_BARBUDA: {
    value: 'AG',
    text: 'Antigua and Barbuda',
  },
  ANGUILLA: {
    value: 'AI',
    text: 'Anguilla',
  },
  ALBANIA: {
    value: 'AL',
    text: 'Albania',
  },
  ARMENIA: {
    value: 'AM',
    text: 'Armenia',
  },
  NETHERLANDS_ANTILLES: {
    value: 'AN',
    text: 'Netherlands Antilles',
  },
  ANGOLA: {
    value: 'AO',
    text: 'Angola',
  },
  ANTARCTICA: {
    value: 'AQ',
    text: 'Antarctica',
  },
  ARGENTINA: {
    value: 'AR',
    text: 'Argentina',
  },
  AMERICAN_SAMOA: {
    value: 'AS',
    text: 'American Samoa',
  },
  AUSTRIA: {
    value: 'AT',
    text: 'Austria',
  },
  AUSTRALIA: {
    value: 'AU',
    text: 'Australia',
  },
  ARUBA: {
    value: 'AW',
    text: 'Aruba',
  },
  ALAND_ISLANDS: {
    value: 'AX',
    text: 'Åland Islands',
  },
  AZERBAIJAN: {
    value: 'AZ',
    text: 'Azerbaijan',
  },
  BOSNIA_AND_HERZEGOVINA: {
    value: 'BA',
    text: 'Bosnia and Herzegovina',
  },
  BARBADOS: {
    value: 'BB',
    text: 'Barbados',
  },
  BANGLADESH: {
    value: 'BD',
    text: 'Bangladesh',
  },
  BELGIUM: {
    value: 'BE',
    text: 'Belgium',
  },
  BURKINA_FASO: {
    value: 'BF',
    text: 'Burkina Faso',
  },
  BULGARIA: {
    value: 'BG',
    text: 'Bulgaria',
  },
  BAHRAIN: {
    value: 'BH',
    text: 'Bahrain',
  },
  BURUNDI: {
    value: 'BI',
    text: 'Burundi',
  },
  BENIN: {
    value: 'BJ',
    text: 'Benin',
  },
  SAINT_BARTHELEMY: {
    value: 'BL',
    text: 'Saint Barthélemy',
  },
  BERMUDA: {
    value: 'BM',
    text: 'Bermuda',
  },
  BRUNEI_DARUSSALAM: {
    value: 'BN',
    text: 'Brunei Darussalam',
  },
  BOLIVIA: {
    value: 'BO',
    text: 'Bolivia (Plurinational State of)',
  },
  BONAIRE: {
    value: 'BQ',
    text: 'Bonaire (Sint Eustatius and Saba)',
  },
  BRAZIL: {
    value: 'BR',
    text: 'Brazil',
  },
  BAHAMAS: {
    value: 'BS',
    text: 'Bahamas',
  },
  BHUTAN: {
    value: 'BT',
    text: 'Bhutan',
  },
  BOUVET_ISLAND: {
    value: 'BV',
    text: 'Bouvet Island',
  },
  BOTSWANA: {
    value: 'BW',
    text: 'Botswana',
  },
  BELARUS: {
    value: 'BY',
    text: 'Belarus',
  },
  BELIZE: {
    value: 'BZ',
    text: 'Belize',
  },
  COCOS_ISLANDS: {
    value: 'CC',
    text: 'Cocos (Keeling) Islands',
  },
  CONGO_DEMOCRATIC_REPUBLIC: {
    value: 'CD',
    text: 'Congo (the Democratic Republic of the)',
  },
  CENTRAL_AFRICAN_REPUBLIC: {
    value: 'CF',
    text: 'Central African Republic',
  },
  CONGO: {
    value: 'CG',
    text: 'Congo (the)',
  },
  SWITZERLAND: {
    value: 'CH',
    text: 'Switzerland',
  },
  COTE_DIVOIRE: {
    value: 'CI',
    text: "Cote d'Ivoire",
  },
  COOK_ISLANDS: {
    value: 'CK',
    text: 'Cook Islands',
  },
  CHILE: {
    value: 'CL',
    text: 'Chile',
  },
  CAMEROON: {
    value: 'CM',
    text: 'Cameroon',
  },
  CHINA: {
    value: 'CN',
    text: 'China',
  },
  COLOMBIA: {
    value: 'CO',
    text: 'Colombia',
  },
  COSTA_RICA: {
    value: 'CR',
    text: 'Costa Rica',
  },
  SERBIA_AND_MONTENEGRO: {
    value: 'CS',
    text: 'Serbia and Montenegro',
  },
  CUBA: {
    value: 'CU',
    text: 'Cuba',
  },
  CABO_VERDE: {
    value: 'CV',
    text: 'Cabo Verde',
  },
  CURACAO: {
    value: 'CW',
    text: 'Curaçao',
  },
  CHRISTMAS_ISLAND: {
    value: 'CX',
    text: 'Christmas Island',
  },
  CYPRUS: {
    value: 'CY',
    text: 'Cyprus',
  },
  CZECH_REPUBLIC: {
    value: 'CZ',
    text: 'Czech Republic',
  },
  GERMANY: {
    value: 'DE',
    text: 'Germany',
  },
  DJIBOUTI: {
    value: 'DJ',
    text: 'Djibouti',
  },
  DENMARK: {
    value: 'DK',
    text: 'Denmark',
  },
  DOMINICA: {
    value: 'DM',
    text: 'Dominica',
  },
  DOMINICAN_REPUBLIC: {
    value: 'DO',
    text: 'Dominican Republic',
  },
  ALGERIA: {
    value: 'DZ',
    text: 'Algeria',
  },
  ECUADOR: {
    value: 'EC',
    text: 'Ecuador',
  },
  ESTONIA: {
    value: 'EE',
    text: 'Estonia',
  },
  EGYPT: {
    value: 'EG',
    text: 'Egypt',
  },
  WESTERN_SAHARA: {
    value: 'EH',
    text: 'Western Sahara',
  },
  ERITREA: {
    value: 'ER',
    text: 'Eritrea',
  },
  SPAIN: {
    value: 'ES',
    text: 'Spain',
  },
  ETHIOPIA: {
    value: 'ET',
    text: 'Ethiopia',
  },
  FINLAND: {
    value: 'FI',
    text: 'Finland',
  },
  FIJI: {
    value: 'FJ',
    text: 'Fiji',
  },
  FALKLAND_ISLANDS: {
    value: 'FK',
    text: 'Falkland Islands (Malvinas)',
  },
  MICRONESIA: {
    value: 'FM',
    text: 'Micronesia (Federated States of)',
  },
  FAROE_ISLANDS: {
    value: 'FO',
    text: 'Faroe Islands',
  },
  FRANCE: {
    value: 'FR',
    text: 'France',
  },
  FRANCE_METROPOLITAN: {
    value: 'FX',
    text: 'France, Metropolitan',
  },
  GABON: {
    value: 'GA',
    text: 'Gabon',
  },
  UNITED_KINGDOM: {
    value: 'GB',
    text: 'United Kingdom (the)',
  },
  GRENADA: {
    value: 'GD',
    text: 'Grenada',
  },
  GEORGIA: {
    value: 'GE',
    text: 'Georgia',
  },
  FRENCH_GUIANA: {
    value: 'GF',
    text: 'French Guiana',
  },
  GUERNSEY: {
    value: 'GG',
    text: 'Guernsey, C.I.',
  },
  GHANA: {
    value: 'GH',
    text: 'Ghana',
  },
  GIBRALTAR: {
    value: 'GI',
    text: 'Gibraltar',
  },
  GREENLAND: {
    value: 'GL',
    text: 'Greenland',
  },
  GAMBIA: {
    value: 'GM',
    text: 'Gambia',
  },
  GUINEA: {
    value: 'GN',
    text: 'Guinea',
  },
  GUADELOUPE: {
    value: 'GP',
    text: 'Guadeloupe',
  },
  EQUATORIAL_GUINEA: {
    value: 'GQ',
    text: 'Equatorial Guinea',
  },
  GREECE: {
    value: 'GR',
    text: 'Greece',
  },
  SOUTH_GEORGIA_AND_SOUTH_SANDWICH_ISLANDS: {
    value: 'GS',
    text: 'So. Georgia and So. Sandwich Islands',
  },
  GUATEMALA: {
    value: 'GT',
    text: 'Guatemala',
  },
  GUAM: {
    value: 'GU',
    text: 'Guam',
  },
  GUINEA_BISSAU: {
    value: 'GW',
    text: 'Guinea-Bissau',
  },
  GUYANA: {
    value: 'GY',
    text: 'Guyana',
  },
  HONG_KONG: {
    value: 'HK',
    text: 'Hong Kong',
  },
  HEARD_AND_MCDONALD_ISLANDS: {
    value: 'HM',
    text: 'Heard and McDonald Islands',
  },
  HONDURAS: {
    value: 'HN',
    text: 'Honduras',
  },
  CROATIA: {
    value: 'HR',
    text: 'Croatia (Hrvatska)',
  },
  HAITI: {
    value: 'HT',
    text: 'Haiti',
  },
  HUNGARY: {
    value: 'HU',
    text: 'Hungary',
  },
  INDONESIA: {
    value: 'ID',
    text: 'Indonesia',
  },
  IRELAND: {
    value: 'IE',
    text: 'Ireland',
  },
  ISRAEL: {
    value: 'IL',
    text: 'Israel',
  },
  ISLE_OF_MAN: {
    value: 'IM',
    text: 'Isle of Man',
  },
  INDIA: {
    value: 'IN',
    text: 'India',
  },
  BRITISH_INDIAN_OCEAN_TERRITORY: {
    value: 'IO',
    text: 'British Indian Ocean Territory',
  },
  IRAQ: {
    value: 'IQ',
    text: 'Iraq',
  },
  IRAN: {
    value: 'IR',
    text: 'Iran (Islamic Republic of)',
  },
  ICELAND: {
    value: 'IS',
    text: 'Iceland',
  },
  ITALY: {
    value: 'IT',
    text: 'Italy',
  },
  JERSEY: {
    value: 'JE',
    text: 'Jersey, C.I.',
  },
  JAMAICA: {
    value: 'JM',
    text: 'Jamaica',
  },
  JORDAN: {
    value: 'JO',
    text: 'Jordan',
  },
  JAPAN: {
    value: 'JP',
    text: 'Japan',
  },
  KENYA: {
    value: 'KE',
    text: 'Kenya',
  },
  KYRGYZSTAN: {
    value: 'KG',
    text: 'Kyrgystan',
  },
  CAMBODIA: {
    value: 'KH',
    text: 'Cambodia',
  },
  KIRIBATI: {
    value: 'KI',
    text: 'Kiribati',
  },
  COMOROS: {
    value: 'KM',
    text: 'Comoros (the)',
  },
  SAINT_KITTS_AND_NEVIS: {
    value: 'KN',
    text: 'Saint Kitts and Nevis',
  },
  KOREA_NORTH: {
    value: 'KP',
    text: "Korea (Dem. People's Rep. of)",
  },
  KOREA_SOUTH: {
    value: 'KR',
    text: 'Korea (the Republic of)',
  },
  KUWAIT: {
    value: 'KW',
    text: 'Kuwait',
  },
  CAYMAN_ISLANDS: {
    value: 'KY',
    text: 'Cayman Islands',
  },
  KAZAKHSTAN: {
    value: 'KZ',
    text: 'Kazakhstan',
  },
  LAO_PEOPLES_DEMOCRATIC_REPUBLIC: {
    value: 'LA',
    text: "Lao People's Dem. Republic",
  },
  LEBANON: {
    value: 'LB',
    text: 'Lebanon',
  },
  SAINT_LUCIA: {
    value: 'LC',
    text: 'Saint Lucia',
  },
  LIECHTENSTEIN: {
    value: 'LI',
    text: 'Liechtenstein',
  },
  SRI_LANKA: {
    value: 'LK',
    text: 'Sri Lanka',
  },
  LIBERIA: {
    value: 'LR',
    text: 'Liberia',
  },
  LESOTHO: {
    value: 'LS',
    text: 'Lesotho',
  },
  LITHUANIA: {
    value: 'LT',
    text: 'Lithuania',
  },
  LUXEMBOURG: {
    value: 'LU',
    text: 'Luxembourg',
  },
  LATVIA: {
    value: 'LV',
    text: 'Latvia',
  },
  LIBYAN_ARAB_JAMAHIRIYA: {
    value: 'LY',
    text: 'Libyan Arab Jamahiriya',
  },
  MOROCCO: {
    value: 'MA',
    text: 'Morocco',
  },
  MONACO: {
    value: 'MC',
    text: 'Monaco',
  },
  MOLDOVA: {
    value: 'MD',
    text: 'Moldova (the Republic of)',
  },
  MONTENEGRO: {
    value: 'ME',
    text: 'Montenegro',
  },
  SAINT_MARTIN_FRENCH: {
    value: 'MF',
    text: 'Saint Martin (French part)',
  },
  MADAGASCAR: {
    value: 'MG',
    text: 'Madagascar',
  },
  MARSHALL_ISLANDS: {
    value: 'MH',
    text: 'Marshall Islands',
  },
  MACEDONIA: {
    value: 'MK',
    text: 'Macedonia (Former Yugoslav Republic)',
  },
  MALI: {
    value: 'ML',
    text: 'Mali',
  },
  MYANMAR: {
    value: 'MM',
    text: 'Myanmar',
  },
  MONGOLIA: {
    value: 'MN',
    text: 'Mongolia',
  },
  MACAO: {
    value: 'MO',
    text: 'Macao',
  },
  NORTHERN_MARIANA_ISLANDS: {
    value: 'MP',
    text: 'Northern Mariana Islands',
  },
  MARTINIQUE: {
    value: 'MQ',
    text: 'Martinique',
  },
  MAURITANIA: {
    value: 'MR',
    text: 'Mauritania',
  },
  MONTSERRAT: {
    value: 'MS',
    text: 'Montserrat',
  },
  MALTA: {
    value: 'MT',
    text: 'Malta',
  },
  MAURITIUS: {
    value: 'MU',
    text: 'Mauritius',
  },
  MALDIVES: {
    value: 'MV',
    text: 'Maldives',
  },
  MALAWI: {
    value: 'MW',
    text: 'Malawi',
  },
  MALAYSIA: {
    value: 'MY',
    text: 'Malaysia',
  },
  MOZAMBIQUE: {
    value: 'MZ',
    text: 'Mozambique',
  },
  NAMIBIA: {
    value: 'NA',
    text: 'Namibia',
  },
  NEW_CALEDONIA: {
    value: 'NC',
    text: 'New Caledonia',
  },
  NIGER: {
    value: 'NE',
    text: 'Niger',
  },
  NORFOLK_ISLAND: {
    value: 'NF',
    text: 'Norfolk Island',
  },
  NIGERIA: {
    value: 'NG',
    text: 'Nigeria',
  },
  NICARAGUA: {
    value: 'NI',
    text: 'Nicaragua',
  },
  NETHERLANDS: {
    value: 'NL',
    text: 'Netherlands',
  },
  NORWAY: {
    value: 'NO',
    text: 'Norway',
  },
  NEPAL: {
    value: 'NP',
    text: 'Nepal',
  },
  NAURU: {
    value: 'NR',
    text: 'Nauru',
  },
  NIUE: {
    value: 'NU',
    text: 'Niue',
  },
  NEW_ZEALAND: {
    value: 'NZ',
    text: 'New Zealand',
  },
  OMAN: {
    value: 'OM',
    text: 'Oman',
  },
  PANAMA: {
    value: 'PA',
    text: 'Panama',
  },
  PERU: {
    value: 'PE',
    text: 'Peru',
  },
  FRENCH_POLYNESIA: {
    value: 'PF',
    text: 'French Polynesia',
  },
  PAPUA_NEW_GUINEA: {
    value: 'PG',
    text: 'Papua New Guinea',
  },
  PHILIPPINES: {
    value: 'PH',
    text: 'Philippines',
  },
  PAKISTAN: {
    value: 'PK',
    text: 'Pakistan',
  },
  POLAND: {
    value: 'PL',
    text: 'Poland',
  },
  SAINT_PIERRE_AND_MIQUELON: {
    value: 'PM',
    text: 'Saint Pierre and Miquelon',
  },
  PITCAIRN: {
    value: 'PN',
    text: 'Pitcairn',
  },
  PUERTO_RICO: {
    value: 'PR',
    text: 'Puerto Rico',
  },
  PALESTINE: {
    value: 'PS',
    text: 'Palestine (State of)',
  },
  PORTUGAL: {
    value: 'PT',
    text: 'Portugal',
  },
  PALAU: {
    value: 'PW',
    text: 'Palau',
  },
  PARAGUAY: {
    value: 'PY',
    text: 'Paraguay',
  },
  QATAR: {
    value: 'QA',
    text: 'Qatar',
  },
  REUNION_ISLAND: {
    value: 'RE',
    text: 'Reunion Island',
  },
  ROMANIA: {
    value: 'RO',
    text: 'Romania',
  },
  SERBIA: {
    value: 'RS',
    text: 'Serbia',
  },
  RUSSIAN_FEDERATION: {
    value: 'RU',
    text: 'Russian Federation (the)',
  },
  RWANDA: {
    value: 'RW',
    text: 'Rwanda',
  },
  SAUDI_ARABIA: {
    value: 'SA',
    text: 'Saudi Arabia',
  },
  SOLOMON_ISLANDS: {
    value: 'SB',
    text: 'Solomon Islands',
  },
  SEYCHELLES: {
    value: 'SC',
    text: 'Seychelles',
  },
  SUDAN: {
    value: 'SD',
    text: 'Sudan',
  },
  SWEDEN: {
    value: 'SE',
    text: 'Sweden',
  },
  SINGAPORE: {
    value: 'SG',
    text: 'Singapore',
  },
  SAINT_HELENA: {
    value: 'SH',
    text: 'Saint Helena',
  },
  SLOVENIA: {
    value: 'SI',
    text: 'Slovenia',
  },
  SVALBARD_AND_JAN_MAYEN_ISLANDS: {
    value: 'SJ',
    text: 'Svalbard and Jan Mayen Islands',
  },
  SLOVAKIA: {
    value: 'SK',
    text: 'Slovakia (Slovak Republic)',
  },
  SIERRA_LEONE: {
    value: 'SL',
    text: 'Sierra Leone',
  },
  SAN_MARINO: {
    value: 'SM',
    text: 'San Marino',
  },
  SENEGAL: {
    value: 'SN',
    text: 'Senegal',
  },
  SOMALIA: {
    value: 'SO',
    text: 'Somalia',
  },
  SURINAME: {
    value: 'SR',
    text: 'Suriname',
  },
  SOUTH_SUDAN: {
    value: 'SS',
    text: 'South Sudan',
  },
  SAO_TOME_AND_PRINCIPE: {
    value: 'ST',
    text: 'Sao Tome and Principe',
  },
  EL_SALVADOR: {
    value: 'SV',
    text: 'El Salvador',
  },
  SINT_MAARTEN: {
    value: 'SX',
    text: 'Sint Maarten (Dutch part)',
  },
  SYRIAN_ARAB_REPUBLIC: {
    value: 'SY',
    text: 'Syrian Arab Republic',
  },
  ESWATINI: {
    value: 'SZ',
    text: 'Eswatini',
  },
  TURKS_AND_CAICOS_ISLANDS: {
    value: 'TC',
    text: 'Turks and Caicos Islands (the)',
  },
  CHAD: {
    value: 'TD',
    text: 'Chad',
  },
  FRENCH_SOUTHERN_TERRITORIES: {
    value: 'TF',
    text: 'French Southern Territories',
  },
  TOGO: {
    value: 'TG',
    text: 'Togo',
  },
  THAILAND: {
    value: 'TH',
    text: 'Thailand',
  },
  TAJIKISTAN: {
    value: 'TJ',
    text: 'Tajikistan',
  },
  TOKELAU: {
    value: 'TK',
    text: 'Tokelau',
  },
  TIMOR_LESTE: {
    value: 'TL',
    text: 'Timor-Leste',
  },
  TURKMENISTAN: {
    value: 'TM',
    text: 'Turkmenistan',
  },
  TUNISIA: {
    value: 'TN',
    text: 'Tunisia',
  },
  TONGA: {
    value: 'TO',
    text: 'Tonga',
  },
  TURKEY: {
    value: 'TR',
    text: 'Turkey',
  },
  TRINIDAD_AND_TOBAGO: {
    value: 'TT',
    text: 'Trinidad and Tobago',
  },
  TUVALU: {
    value: 'TV',
    text: 'Tuvalu',
  },
  TAIWAN: {
    value: 'TW',
    text: 'Taiwan',
  },
  TANZANIA: {
    value: 'TZ',
    text: 'Tanzania (United Republic of)',
  },
  UKRAINE: {
    value: 'UA',
    text: 'Ukraine',
  },
  UGANDA: {
    value: 'UG',
    text: 'Uganda',
  },
  UNITED_STATES_MINOR_OUTLYING_ISLANDS: {
    value: 'UM',
    text: "United States Minor Outlying Is.'s",
  },
  URUGUAY: {
    value: 'UY',
    text: 'Uruguay',
  },
  UZBEKISTAN: {
    value: 'UZ',
    text: 'Uzbekistan',
  },
  HOLY_SEE: {
    value: 'VA',
    text: 'Holy See (the)',
  },
  SAINT_VINCENT_AND_THE_GRENADINES: {
    value: 'VC',
    text: 'Saint Vincent and the Grenadines',
  },
  VENEZUELA: {
    value: 'VE',
    text: 'Venezuela (Bolivarian Republic of)',
  },
  VIRGIN_ISLANDS_BRITISH: {
    value: 'VG',
    text: 'Virgin Islands (British)',
  },
  VIRGIN_ISLANDS_US: {
    value: 'VI',
    text: 'Virgin Islands (U.S.)',
  },
  VIET_NAM: {
    value: 'VN',
    text: 'Viet Nam',
  },
  VANUATU: {
    value: 'VU',
    text: 'Vanuatu',
  },
  WALLIS_AND_FUTUNA_ISLANDS: {
    value: 'WF',
    text: 'Wallis and Futuna Islands',
  },
  SAMOA: {
    value: 'WS',
    text: 'Samoa',
  },
  YEMEN: {
    value: 'YE',
    text: 'Yemen',
  },
  MAYOTTE: {
    value: 'YT',
    text: 'Mayotte',
  },
  SOUTH_AFRICA: {
    value: 'ZA',
    text: 'South Africa',
  },
  ZAMBIA: {
    value: 'ZM',
    text: 'Zambia',
  },
  ZIMBABWE: {
    value: 'ZW',
    text: 'Zimbabwe',
  },
  UNKNOWN: {
    value: 'ZZ',
    text: 'Unknown',
  },
} as const satisfies Record<string, U21SelectOptionProps<string>>;

export const FINTRAC_STR_COUNTRY_CODE_OPTIONS = generateOptions<string>(
  FINTRAC_STR_COUNTRY_CODE,
);

const FINTRAC_STR_CANADA_STATE_CODE = {
  ALBERTA: {
    value: 'AB',
    text: 'Alberta',
  },
  BRITISH_COLUMBIA: {
    value: 'BC',
    text: 'British Columbia',
  },
  MANITOBA: {
    value: 'MB',
    text: 'Manitoba',
  },
  NEW_BRUNSWICK: {
    value: 'NB',
    text: 'New Brunswick',
  },
  NEWFOUNDLAND_AND_LABRADOR: {
    value: 'NL',
    text: 'Newfoundland and Labrador',
  },
  NORTHWEST_TERRITORIES: {
    value: 'NT',
    text: 'Northwest Territories',
  },
  NOVA_SCOTIA: {
    value: 'NS',
    text: 'Nova Scotia',
  },
  NUNAVUT: {
    value: 'NU',
    text: 'Nunavut',
  },
  ONTARIO: {
    value: 'ON',
    text: 'Ontario',
  },
  PRINCE_EDWARD_ISLAND: {
    value: 'PE',
    text: 'Prince Edward Island',
  },
  QUEBEC: {
    value: 'QC',
    text: 'Quebec',
  },
  SASKATCHEWAN: {
    value: 'SK',
    text: 'Saskatchewan',
  },
  YUKON: {
    value: 'YT',
    text: 'Yukon',
  },
} as const satisfies Record<string, U21SelectOptionProps<string>>;

export const FINTRAC_STR_CANADA_STATE_CODE_OPTIONS = generateOptions<string>(
  FINTRAC_STR_CANADA_STATE_CODE,
);

export const FINTRAC_STR_UNITED_STATES_STATE_CODE = {
  ALABAMA: {
    value: 'AL',
    text: 'Alabama',
  },
  ALASKA: {
    value: 'AK',
    text: 'Alaska',
  },
  ARIZONA: {
    value: 'AZ',
    text: 'Arizona',
  },
  ARKANSAS: {
    value: 'AR',
    text: 'Arkansas',
  },
  CALIFORNIA: {
    value: 'CA',
    text: 'California',
  },
  COLORADO: {
    value: 'CO',
    text: 'Colorado',
  },
  CONNECTICUT: {
    value: 'CT',
    text: 'Connecticut',
  },
  DELAWARE: {
    value: 'DE',
    text: 'Delaware',
  },
  DISTRICT_OF_COLUMBIA: {
    value: 'DC',
    text: 'District of Columbia',
  },
  FLORIDA: {
    value: 'FL',
    text: 'Florida',
  },
  GEORGIA: {
    value: 'GA',
    text: 'Georgia',
  },
  HAWAII: {
    value: 'HI',
    text: 'Hawaii',
  },
  IDAHO: {
    value: 'ID',
    text: 'Idaho',
  },
  ILLINOIS: {
    value: 'IL',
    text: 'Illinois',
  },
  INDIANA: {
    value: 'IN',
    text: 'Indiana',
  },
  IOWA: {
    value: 'IA',
    text: 'Iowa',
  },
  KANSAS: {
    value: 'KS',
    text: 'Kansas',
  },
  KENTUCKY: {
    value: 'KY',
    text: 'Kentucky',
  },
  LOUISIANA: {
    value: 'LA',
    text: 'Louisiana',
  },
  MAINE: {
    value: 'ME',
    text: 'Maine',
  },
  MARYLAND: {
    value: 'MD',
    text: 'Maryland',
  },
  MASSACHUSETTS: {
    value: 'MA',
    text: 'Massachusetts',
  },
  MICHIGAN: {
    value: 'MI',
    text: 'Michigan',
  },
  MINNESOTA: {
    value: 'MN',
    text: 'Minnesota',
  },
  MISSISSIPPI: {
    value: 'MS',
    text: 'Mississippi',
  },
  MISSOURI: {
    value: 'MO',
    text: 'Missouri',
  },
  MONTANA: {
    value: 'MT',
    text: 'Montana',
  },
  NEBRASKA: {
    value: 'NE',
    text: 'Nebraska',
  },
  NEVADA: {
    value: 'NV',
    text: 'Nevada',
  },
  NEW_HAMPSHIRE: {
    value: 'NH',
    text: 'New Hampshire',
  },
  NEW_JERSEY: {
    value: 'NJ',
    text: 'New Jersey',
  },
  NEW_MEXICO: {
    value: 'NM',
    text: 'New Mexico',
  },
  NEW_YORK: {
    value: 'NY',
    text: 'New York',
  },
  NORTH_CAROLINA: {
    value: 'NC',
    text: 'North Carolina',
  },
  NORTH_DAKOTA: {
    value: 'ND',
    text: 'North Dakota',
  },
  OHIO: {
    value: 'OH',
    text: 'Ohio',
  },
  OKLAHOMA: {
    value: 'OK',
    text: 'Oklahoma',
  },
  OREGON: {
    value: 'OR',
    text: 'Oregon',
  },
  PENNSYLVANIA: {
    value: 'PA',
    text: 'Pennsylvania',
  },
  RHODE_ISLAND: {
    value: 'RI',
    text: 'Rhode Island',
  },
  SOUTH_CAROLINA: {
    value: 'SC',
    text: 'South Carolina',
  },
  SOUTH_DAKOTA: {
    value: 'SD',
    text: 'South Dakota',
  },
  TENNESSEE: {
    value: 'TN',
    text: 'Tennessee',
  },
  TEXAS: {
    value: 'TX',
    text: 'Texas',
  },
  UTAH: {
    value: 'UT',
    text: 'Utah',
  },
  VERMONT: {
    value: 'VT',
    text: 'Vermont',
  },
  VIRGINIA: {
    value: 'VA',
    text: 'Virginia',
  },
  WASHINGTON: {
    value: 'WA',
    text: 'Washington',
  },
  WEST_VIRGINIA: {
    value: 'WV',
    text: 'West Virginia',
  },
  WISCONSIN: {
    value: 'WI',
    text: 'Wisconsin',
  },
  WYOMING: {
    value: 'WY',
    text: 'Wyoming',
  },
} as const satisfies Record<string, U21SelectOptionProps<string>>;

export const FINTRAC_STR_UNITED_STATES_STATE_CODE_OPTIONS =
  generateOptions<string>(FINTRAC_STR_UNITED_STATES_STATE_CODE);

export const FINTRAC_STR_MEXICO_STATE_CODE = {
  AGUASCALIENTES: {
    value: 'AG',
    text: 'Aguascalientes',
  },
  BAJA_CALIFORNIA_NORTE: {
    value: 'BA',
    text: 'Baja California Norte',
  },
  BAJA_CALIFORNIA_SUR: {
    value: 'BJ',
    text: 'Baja California Sur',
  },
  CAMPECHE: {
    value: 'CE',
    text: 'Campeche',
  },
  CHIAPAS: {
    value: 'CI',
    text: 'Chiapas',
  },
  CHIHUAHUA: {
    value: 'CH',
    text: 'Chihuahua',
  },
  COAHUILA_DE_ZARAGOZA: {
    value: 'CU',
    text: 'Coahuila de Zaragoza',
  },
  COLIMA: {
    value: 'CL',
    text: 'Colima',
  },
  DISTRITO_FEDERAL: {
    value: 'DF',
    text: 'Distrito Federal',
  },
  DURANGO: {
    value: 'DO',
    text: 'Durango',
  },
  GUANAJUATO: {
    value: 'GU',
    text: 'Guanajuato',
  },
  GUERRERO: {
    value: 'GR',
    text: 'Guerrero',
  },
  HIDALGO: {
    value: 'HL',
    text: 'Hidalgo',
  },
  JALISCO: {
    value: 'JL',
    text: 'Jalisco',
  },
  MEXICO_STATE: {
    value: 'MX',
    text: 'Mexico (State)',
  },
  MICHOACAN_DE_OCAMPO: {
    value: 'MC',
    text: 'Michoacán de Ocampo',
  },
  MORELOS: {
    value: 'MR',
    text: 'Morelos',
  },
  NAYARIT: {
    value: 'NA',
    text: 'Nayarit',
  },
  NUEVO_LEON: {
    value: 'NL',
    text: 'Nuevo León',
  },
  OAXACA: {
    value: 'OA',
    text: 'Oaxaca',
  },
  PUEBLA: {
    value: 'PB',
    text: 'Puebla',
  },
  QUERETARO_DE_ARTEAGA: {
    value: 'QU',
    text: 'Querétaro de Arteaga',
  },
  QUINTANA_ROO: {
    value: 'QR',
    text: 'Quintana Roo',
  },
  SAN_LUIS_POTOSI: {
    value: 'SL',
    text: 'San Luis Potosí',
  },
  SINALOA: {
    value: 'SI',
    text: 'Sinaloa',
  },
  SONORA: {
    value: 'SO',
    text: 'Sonora',
  },
  TABASCO: {
    value: 'TB',
    text: 'Tabasco',
  },
  TAMAULIPAS: {
    value: 'TA',
    text: 'Tamaulipas',
  },
  TLAXCALA: {
    value: 'TL',
    text: 'Tlaxcala',
  },
  VERACRUZ_LLAVE: {
    value: 'VC',
    text: 'Veracruz-Llave',
  },
  YUCATAN: {
    value: 'YU',
    text: 'Yucatán',
  },
  ZACATECAS: {
    value: 'ZA',
    text: 'Zacatecas',
  },
} as const satisfies Record<string, U21SelectOptionProps<string>>;

export const FINTRAC_STR_MEXICO_STATE_CODE_OPTIONS = generateOptions<string>(
  FINTRAC_STR_MEXICO_STATE_CODE,
);

export const FINTRAC_STR_PERSON_IDENTIFIER_TYPE_CODE = {
  BIRTH_CERTIFICATE: {
    value: 1,
    text: 'Birth certificate',
  },
  PASSPORT: {
    value: 2,
    text: 'Passport',
  },
  OTHER: {
    value: 3,
    text: 'Other',
  },
  DRIVERS_LICENCE: {
    value: 4,
    text: "Driver's licence",
  },
  PROVINCIAL_HEALTH_CARD: {
    value: 5,
    text: 'Provincial health card',
  },
  CITIZENSHIP_CARD: {
    value: 14,
    text: 'Citizenship card',
  },
  CERTIFICATE_OF_INDIAN_STATUS: {
    value: 15,
    text: 'Certificate of Indian Status',
  },
  SOCIAL_INSURANCE_NUMBER_CARD: {
    value: 27,
    text: 'Social Insurance Number card',
  },
  PERMANENT_RESIDENT_CARD: {
    value: 32,
    text: 'Permanent resident card',
  },
  RECORD_OF_LANDING: {
    value: 33,
    text: 'Record of landing',
  },
  CREDIT_FILE: {
    value: 34,
    text: 'Credit file',
  },
  GOVERNMENT_ISSUED_IDENTIFICATION: {
    value: 35,
    text: 'Government issued identification',
  },
  INSURANCE_DOCUMENTS: {
    value: 36,
    text: 'Insurance documents',
  },
  PROVINCIAL_OR_TERRITORIAL_IDENTITY_CARD: {
    value: 37,
    text: 'Provincial or territorial identity card',
  },
  RECORD_OF_EMPLOYMENT: {
    value: 38,
    text: 'Record of employment',
  },
  TRAVEL_VISA: {
    value: 39,
    text: 'Travel visa',
  },
  UTILITY_STATEMENT: {
    value: 40,
    text: 'Utility statement',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_PERSON_IDENTIFIER_TYPE_CODE_OPTIONS =
  generateOptions<number>(FINTRAC_STR_PERSON_IDENTIFIER_TYPE_CODE);

export const FINTRAC_STR_ENTITY_IDENTIFIER_TYPE_CODE = {
  ARTICLES_OF_ASSOCIATION: {
    value: 1,
    text: 'Articles of association',
  },
  CERTIFICATE_OF_CORPORATE_STATUS: {
    value: 2,
    text: 'Certificate of corporate status',
  },
  CERTIFICATE_OF_INCORPORATION: {
    value: 3,
    text: 'Certificate of incorporation',
  },
  LETTER_NOTICE_OF_ASSESSMENT: {
    value: 4,
    text: 'Letter/Notice of assessment',
  },
  PARTNERSHIP_AGREEMENT: {
    value: 5,
    text: 'Partnership agreement',
  },
  ANNUAL_REPORT: {
    value: 6,
    text: 'Annual report',
  },
  OTHER: {
    value: 7,
    text: 'Other',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_ENTITY_IDENTIFIER_TYPE_CODE_OPTIONS =
  generateOptions<number>(FINTRAC_STR_ENTITY_IDENTIFIER_TYPE_CODE);

export const FINTRAC_STR_INCORPORATION_TYPE_CODE = {
  REGISTERED: {
    value: 1,
    text: 'Registered',
  },
  INCORPORATED: {
    value: 2,
    text: 'Incorporated',
  },
  REGISTERED_AND_INCORPORATED: {
    value: 4,
    text: 'Registered and incorporated',
  },
  UNKNOWN: {
    value: 5,
    text: 'Unknown',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_INCORPORATION_TYPE_CODE_OPTIONS =
  generateOptions<number>(FINTRAC_STR_INCORPORATION_TYPE_CODE);

export const FINTRAC_STR_STRUCTURE_TYPE_CODE = {
  CORPORATION: {
    value: 1,
    text: 'Corporation',
  },
  ENTITY_OTHER_THAN_CORPORATION_OR_TRUST: {
    value: 2,
    text: 'Entity other than a corporation or trust',
  },
  TRUST: {
    value: 3,
    text: 'Trust',
  },
  WIDELY_HELD_OR_PUBLICLY_TRADED_TRUST: {
    value: 4,
    text: 'Widely held or publicly traded trust',
  },
} as const satisfies Record<string, U21SelectOptionProps<number>>;

export const FINTRAC_STR_STRUCTURE_TYPE_CODE_OPTIONS = generateOptions<number>(
  FINTRAC_STR_STRUCTURE_TYPE_CODE,
);
